<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import TalkviaFunnelsAPI from 'dashboard/api/talkviaFunnels';
import TalkviaCreditEnginesAPI from 'dashboard/api/talkviaCreditEngines';

const NODE_WIDTH = 320;
const NODE_HEIGHT = 206;
const MIN_ZOOM = 0.45;
const MAX_ZOOM = 1.45;
const selectClass =
  'w-full appearance-none bg-none rounded-lg border-0 outline-1 outline -outline-offset-1 transition-all duration-200 bg-n-surface-1 !mb-0 py-2 px-3 pr-10 text-sm outline-n-slate-5 dark:outline-n-weak hover:outline-n-slate-6 focus:outline-n-brand';
const inputClass =
  'w-full flex reset-base text-sm h-10 !mb-0 border-0 rounded-lg outline-1 outline -outline-offset-1 bg-n-surface-1 px-3 text-n-slate-12 transition-all duration-200 outline-n-slate-5 dark:outline-n-weak hover:outline-n-slate-6 focus:outline-n-brand placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50';

const copy = {
  eyebrow: 'Flow builder',
  title: 'Construtor de fluxos Talkvia',
  subtitle:
    'Desenhe automações, funis e ofertas conectando blocos do Talkvia em um canvas visual.',
  actions: {
    addNode: 'Adicionar nó',
    addTrigger: 'Adicionar entrada',
    addWebhookTrigger: 'Adicionar webhook',
    useWebhookTrigger: 'Usar webhook nesta entrada',
    addNextAction: 'Adicionar próxima ação',
    testFlow: 'Testar fluxo',
    clearTest: 'Limpar teste',
    newProject: 'Novo projeto',
    connect: 'Conectar',
    cancelConnect: 'Cancelar conexão',
    save: 'Salvar',
    duplicate: 'Duplicar',
    remove: 'Remover',
    reset: 'Restaurar exemplo',
    fit: 'Centralizar',
    openLink: 'Abrir atalho',
    close: 'Fechar',
    copyWebhookUrl: 'Copiar URL',
    refreshWebhook: 'Buscar último JSON',
    waitWebhookTest: 'Aguardar webhook e testar',
    editNode: 'Editar nó',
    collapsePanel: 'Recolher painel',
    expandPanel: 'Abrir painel',
  },
  status: {
    loading: 'Carregando flow...',
    saving: 'Salvando...',
    saved: 'Salvo',
    error: 'Não foi possível salvar',
  },
  metrics: {
    nodes: 'Nós',
    edges: 'Conexões',
    leads: 'Leads',
    conversion: 'Conversão média',
  },
  form: {
    name: 'Nome do fluxo',
    nodeTitle: 'Título',
    description: 'Descrição',
    source: 'Origem',
    nodeType: 'Tipo do nó',
    linkType: 'Atalho Talkvia',
    leads: 'Leads',
    conversion: 'Conversão',
    investment: 'Investimento',
    potential: 'Ganho potencial',
    color: 'Cor',
    project: 'Projeto',
    inbox: 'Caixa de entrada',
    status: 'Status do funil',
    actionType: 'Então fazer',
    actionMessage: 'Mensagem/template',
    actionPhonePath: 'Número do contato no JSON',
    actionContactPath: 'Nome do contato no JSON',
    actionMessagePath: 'Mensagem do JSON',
    actionDelay: 'Aguardar minutos',
    creditEngine: 'Motor de crédito',
    actionFile: 'Arquivo para o contato',
    actionFileCaption: 'Legenda do arquivo',
    webhookUrl: 'URL do webhook',
    webhookMethod: 'Método',
    triggerType: 'Quando isso acontecer',
    triggerWebhookEndpoint: 'URL de entrada',
    triggerWebhookMethod: 'Método da entrada',
    triggerWebhookSample: 'JSON de teste',
    triggerWebhookLastPayload: 'Último JSON recebido',
    jsonContactPath: 'Campo do nome',
    jsonPhonePath: 'Campo do telefone',
    jsonMessagePath: 'Campo da mensagem/etiqueta',
    triggerLabel: 'Etiqueta do cliente',
    triggerInbox: 'Caixa de entrada do gatilho',
    triggerKeyword: 'Palavra-chave',
  },
  nodeTypes: {
    trigger: 'Entrada',
    action: 'Ação',
    decision: 'Condição',
    wait: 'Espera',
    offer: 'Oferta',
    end: 'Fim',
  },
  actionTypes: {
    none: 'Apenas etapa visual',
    send_message: 'Enviar mensagem',
    send_file: 'Enviar arquivo',
    add_label: 'Adicionar etiqueta',
    move_inbox: 'Mover para caixa',
    request_best_offer: 'Buscar melhor oferta',
    create_contract: 'Gerar contrato/formalização',
    assign_human: 'Chamar atendimento humano',
    call_webhook: 'Chamar webhook',
    run_credit_engine: 'Executar motor de crédito',
  },
  triggerTypes: {
    conversation_created: 'Cliente iniciou conversa',
    label_added: 'Cliente recebeu etiqueta',
    message_contains: 'Mensagem contém palavra',
    inbox_changed: 'Cliente entrou na caixa',
    webhook_received: 'Webhook recebido',
  },
  linkTypes: {
    conversations: 'Conversas',
    contacts: 'Contatos',
    campaigns: 'Campanhas WhatsApp',
    proposals: 'Propostas',
    calendar: 'Agenda',
    integrations: 'Integrações',
    none: 'Sem atalho',
  },
  labels: {
    selectedNode: 'Nó selecionado',
    selectedEdge: 'Conexão selecionada',
    flowStart: 'Início do fluxo',
    flowStartDescription:
      'Use um bloco Entrada para definir o gatilho real, como cliente com etiqueta X, e conecte ao próximo passo.',
    edgeDefault: 'Conexão',
    edgeLabel: 'Rótulo',
    edgePlaceholder: 'Ex.: sem resposta, aprovado, oferta',
    conversionShort: 'Conv.',
    potentialShort: 'Pot.',
    connectorStart: 'Saída',
    connectorEnd: 'Entrada',
    connectorStartTitle: 'Iniciar conexão a partir deste nó',
    connectorEndTitle: 'Conectar neste nó',
    connectIdle: 'Clique na saída do bloco origem',
    connectActive: 'Agora clique na entrada do bloco destino',
    noInbox: 'Todas as caixas',
    active: 'Ativo',
    inactive: 'Inativo',
    uploadReady: 'Arquivo pronto para envio',
    uploadError: 'Não foi possível enviar o arquivo',
    labelField: 'Etiqueta',
    labelPlaceholder: 'ex.: aguardando_contrato',
    targetInbox: 'Caixa destino',
    triggerSettings: 'Quando',
    actionSettings: 'Então',
    triggerLabelPlaceholder: 'ex.: cliente_vip',
    triggerKeywordPlaceholder: 'ex.: quero simular',
    keyboardHint: 'Delete ou Backspace remove o item selecionado.',
    testRun: 'Teste de execução',
    testIdle: 'Clique em Testar fluxo para simular o caminho da automação.',
    testStarted: 'Entrada disparada',
    testFinished: 'Fluxo finalizado',
    noTriggerToTest: 'Adicione ou selecione uma Entrada para testar.',
    fileCaptionPlaceholder: 'ex.: segue o contrato para análise',
    webhookPlaceholder: 'https://exemplo.com/webhook',
    jsonPathPlaceholder: 'ex.: cliente.telefone',
    jsonDetectedFields: 'Campos detectados',
    jsonLastFields: 'Campos do último JSON',
    webhookCopied: 'URL copiada',
    webhookCopyFailed: 'Não foi possível copiar a URL.',
    webhookNoPayload: 'Nenhum JSON recebido ainda',
    noCreditEngine: 'Selecione um motor',
    noCreditEngines: 'Nenhum motor de crédito cadastrado.',
    creditEngineHelp:
      'O motor avalia os dados do contato, da conversa, da proposta e do webhook. Use nas conexões os rótulos “elegível”, “inelegível” e “dados incompletos”.',
    openCreditEngine: 'Revisar motor de crédito',
    inputColumn: 'Entrada',
    processColumn: 'Processos',
    outputColumn: 'Saída',
    testRunning: 'Executando',
    testSuccess: 'Executado',
    testError: 'Erro',
    testIncomplete: 'Configuração incompleta',
    testModalDescription:
      'Simulação visual: nenhum envio real é disparado durante o teste.',
    webhookWaiting: 'Aguardando entrada pelo webhook',
    webhookWaitingDetail:
      'Envie uma requisição para a URL de entrada. O teste inicia automaticamente quando o JSON chegar.',
    webhookReceived: 'Webhook recebido',
    webhookWaitTimeout:
      'Nenhum webhook recebido no tempo de espera. Envie o JSON e tente novamente.',
    outboundWebhookUnavailable:
      'Webhook de saída ainda não está habilitado com segurança. Substitua esta ação antes de ativar o fluxo.',
    nextNodeTitle: 'O que acontece depois?',
    nextNodeDescription: 'Escolha uma etapa para conectar após este nó.',
    nextNodeSearch: 'Buscar ação...',
    jsonVariablesHint:
      'Use os campos do webhook como variáveis, por exemplo {{cliente.nome}}.',
    sendMessageTarget: 'Destino da mensagem',
    sendMessageTargetHelp:
      'Quando o fluxo começa por webhook, o telefone abaixo define qual contato recebe a mensagem.',
  },
  empty: {
    panel:
      'Selecione um nó para editar detalhes, duplicar ou abrir o atalho vinculado.',
    canvas:
      'Arraste os nós pelo canvas. Use Conectar e clique em dois nós para criar uma ligação.',
  },
};

const nodeTypes = [
  { value: 'trigger', label: copy.nodeTypes.trigger, icon: 'i-lucide-play' },
  { value: 'action', label: copy.nodeTypes.action, icon: 'i-lucide-zap' },
  {
    value: 'decision',
    label: copy.nodeTypes.decision,
    icon: 'i-lucide-git-branch',
  },
  { value: 'wait', label: copy.nodeTypes.wait, icon: 'i-lucide-clock-3' },
  {
    value: 'offer',
    label: copy.nodeTypes.offer,
    icon: 'i-lucide-badge-dollar-sign',
  },
  { value: 'end', label: copy.nodeTypes.end, icon: 'i-lucide-flag' },
];

const cardPalette = [
  { value: 'emerald', label: 'Verde' },
  { value: 'blue', label: 'Azul' },
  { value: 'amber', label: 'Amarelo' },
  { value: 'ruby', label: 'Vermelho' },
  { value: 'violet', label: 'Violeta' },
  { value: 'slate', label: 'Cinza' },
];

const actionOptions = [
  { value: 'none', label: copy.actionTypes.none },
  { value: 'send_message', label: copy.actionTypes.send_message },
  { value: 'send_file', label: copy.actionTypes.send_file },
  { value: 'add_label', label: copy.actionTypes.add_label },
  { value: 'move_inbox', label: copy.actionTypes.move_inbox },
  { value: 'request_best_offer', label: copy.actionTypes.request_best_offer },
  { value: 'create_contract', label: copy.actionTypes.create_contract },
  { value: 'assign_human', label: copy.actionTypes.assign_human },
  { value: 'run_credit_engine', label: copy.actionTypes.run_credit_engine },
];

const triggerOptions = [
  {
    value: 'conversation_created',
    label: copy.triggerTypes.conversation_created,
  },
  { value: 'label_added', label: copy.triggerTypes.label_added },
  { value: 'message_contains', label: copy.triggerTypes.message_contains },
  { value: 'inbox_changed', label: copy.triggerTypes.inbox_changed },
  { value: 'webhook_received', label: copy.triggerTypes.webhook_received },
];

const webhookMethods = ['POST', 'PUT', 'PATCH', 'GET'];

const nextNodeOptions = [
  {
    type: 'action',
    actionType: 'run_credit_engine',
    title: copy.actionTypes.run_credit_engine,
    description: 'Avaliar elegibilidade e seguir pelo resultado do motor.',
    icon: 'i-lucide-gauge',
    color: 'violet',
  },
  {
    type: 'action',
    actionType: 'send_message',
    title: copy.actionTypes.send_message,
    description: 'Enviar uma resposta usando telefone e campos do JSON.',
    icon: 'i-lucide-message-circle',
    color: 'blue',
  },
  {
    type: 'action',
    actionType: 'request_best_offer',
    title: copy.actionTypes.request_best_offer,
    description: 'Marcar o cliente para simulação e melhor oferta.',
    icon: 'i-lucide-landmark',
    color: 'emerald',
  },
  {
    type: 'action',
    actionType: 'create_contract',
    title: copy.actionTypes.create_contract,
    description: 'Avançar o atendimento para geração de contrato.',
    icon: 'i-lucide-file-pen-line',
    color: 'violet',
  },
  {
    type: 'action',
    actionType: 'assign_human',
    title: copy.actionTypes.assign_human,
    description: 'Abrir para atendimento humano no Talkvia.',
    icon: 'i-lucide-user-check',
    color: 'amber',
  },
  {
    type: 'wait',
    actionType: 'none',
    title: copy.nodeTypes.wait,
    description: 'Criar uma pausa visual antes da próxima etapa.',
    icon: 'i-lucide-clock-3',
    color: 'amber',
  },
];

const createWebhookToken = () => {
  const cryptoApi = window.crypto;
  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();
  const randomPart = Math.random().toString(36).slice(2, 14);
  return `tw-${Date.now()}-${randomPart}`;
};

const linkOptions = [
  { value: 'conversations', label: copy.linkTypes.conversations },
  { value: 'contacts', label: copy.linkTypes.contacts },
  { value: 'campaigns', label: copy.linkTypes.campaigns },
  { value: 'proposals', label: copy.linkTypes.proposals },
  { value: 'calendar', label: copy.linkTypes.calendar },
  { value: 'integrations', label: copy.linkTypes.integrations },
  { value: 'none', label: copy.linkTypes.none },
];

const emptyNodeAction = () => ({
  type: 'none',
  message: '',
  label: '',
  targetInboxId: '',
  delayMinutes: 0,
  file: null,
  fileCaption: '',
  webhookUrl: '',
  webhookMethod: 'POST',
  creditEngineId: '',
  contactPath: '',
  phonePath: '',
  messagePath: '',
});

const normalizeNodeAction = action => ({
  ...emptyNodeAction(),
  ...(action && typeof action === 'object' ? action : {}),
});

const emptyNodeTrigger = () => ({
  type: 'conversation_created',
  label: '',
  inboxId: '',
  keyword: '',
  webhookMethod: 'POST',
  webhookToken: createWebhookToken(),
  webhookSample:
    '{\n  "cliente": {\n    "nome": "Maria",\n    "telefone": "5511999999999"\n  },\n  "mensagem": "quero simular"\n}',
  contactPath: 'cliente.nome',
  phonePath: 'cliente.telefone',
  messagePath: 'mensagem',
  lastPayload: null,
  lastFields: [],
  lastReceivedAt: '',
  lastRequest: null,
});

const normalizeNodeTrigger = trigger => ({
  ...emptyNodeTrigger(),
  ...(trigger && typeof trigger === 'object' ? trigger : {}),
});

const makeNode = payload => ({
  id:
    payload.id ||
    `node-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  type: payload.type || 'action',
  title: payload.title || 'Novo nó',
  description: payload.description || '',
  source: payload.source || '',
  leads: Number(payload.leads || 0),
  investment: Number(payload.investment || 0),
  conversion: Number(payload.conversion || 0),
  potential: Number(payload.potential || 0),
  color: payload.color || 'blue',
  linkType: payload.linkType || payload.link_type || 'conversations',
  action: normalizeNodeAction(payload.action || payload.metadata?.action),
  trigger: normalizeNodeTrigger(payload.trigger || payload.metadata?.trigger),
  position: {
    x: Number(payload.position?.x ?? payload.x ?? 120),
    y: Number(payload.position?.y ?? payload.y ?? 120),
  },
});

const defaultFlow = () => ({
  name: 'Funil de Vendas - Compass',
  nodes: [
    makeNode({
      id: 'instagram-ads',
      type: 'trigger',
      title: 'Instagram Ads',
      description: 'Campanhas pagas enviando leads para WhatsApp.',
      source: 'Meta Ads',
      leads: 1087,
      investment: 200000,
      conversion: 50.9,
      potential: 184,
      color: 'blue',
      linkType: 'campaigns',
      trigger: { type: 'label_added', label: 'lead_meta_ads' },
      position: { x: 80, y: 120 },
    }),
    makeNode({
      id: 'organico',
      type: 'trigger',
      title: 'Instagram orgânico',
      description: 'Conteúdo, Reels e Stories conectados ao inbox.',
      source: 'Instagram',
      leads: 547,
      investment: 5000,
      conversion: 25.6,
      potential: 91,
      color: 'emerald',
      linkType: 'conversations',
      trigger: { type: 'conversation_created' },
      position: { x: 80, y: 330 },
    }),
    makeNode({
      id: 'lead-scoring',
      type: 'decision',
      title: 'Lead scoring',
      description: 'Classificação automática para priorizar pré-venda.',
      source: 'Qualifica AI',
      leads: 2137,
      conversion: 70,
      potential: 427,
      color: 'amber',
      linkType: 'contacts',
      position: { x: 500, y: 220 },
    }),
    makeNode({
      id: 'followup',
      type: 'wait',
      title: 'Follow-up automático',
      description: 'Retomada pelo WhatsApp quando o cliente fica inativo.',
      source: 'Talkvia Inbox',
      leads: 1496,
      conversion: 48,
      potential: 312,
      color: 'violet',
      linkType: 'conversations',
      position: { x: 920, y: 220 },
    }),
    makeNode({
      id: 'oferta',
      type: 'offer',
      title: 'Oferta de melhor condição',
      description: 'Busca a tabela mais favorável ou usa a escolha manual.',
      source: 'Integração bancária',
      leads: 718,
      conversion: 34,
      potential: 244,
      color: 'emerald',
      linkType: 'integrations',
      position: { x: 1340, y: 220 },
    }),
  ],
  edges: [
    {
      id: 'edge-ads-score',
      source: 'instagram-ads',
      target: 'lead-scoring',
      label: 'lead',
    },
    {
      id: 'edge-org-score',
      source: 'organico',
      target: 'lead-scoring',
      label: 'lead',
    },
    {
      id: 'edge-score-follow',
      source: 'lead-scoring',
      target: 'followup',
      label: 'sem resposta',
    },
    {
      id: 'edge-follow-offer',
      source: 'followup',
      target: 'oferta',
      label: 'x tempo',
    },
  ],
  viewport: { x: 24, y: 36, zoom: 0.85 },
});

const route = useRoute();
const router = useRouter();
const store = useStore();
const flow = reactive(defaultFlow());
const automationSettings = reactive({ inboxId: '', active: true });
const funnels = ref([]);
const selectedNodeId = ref('');
const selectedEdgeId = ref('');
const currentFunnelId = ref(null);
const creditEngines = ref([]);
const isLoading = ref(true);
const isSaving = ref(false);
const saveError = ref(false);
const hasLoadedRemoteFlow = ref(false);
const isConnecting = ref(false);
const connectionSourceId = ref('');
const canvasEl = ref(null);
const dragState = ref(null);
const panState = ref(null);
const isUploadingFile = ref(false);
const uploadError = ref(false);
const contextMenu = reactive({
  visible: false,
  nodeId: '',
  x: 0,
  y: 0,
});
const nodePicker = reactive({
  visible: false,
  sourceNodeId: '',
  search: '',
});
const isNodeInspectorOpen = ref(false);
const isFlowPanelCollapsed = ref(false);
const testRun = reactive({
  activeNodeId: '',
  isRunning: false,
  isModalOpen: false,
  logs: [],
  statusByNode: {},
});
let saveTimer = null;
let testTimers = [];
let webhookWaitTimer = null;
let webhookWaitStartedAt = null;

const selectedNode = computed(() =>
  flow.nodes.find(node => node.id === selectedNodeId.value)
);

const contextMenuNode = computed(() =>
  flow.nodes.find(node => node.id === contextMenu.nodeId)
);

const inboxes = useMapGetter('inboxes/getAllInboxes');
const creditEngineOptions = computed(() =>
  (creditEngines.value || [])
    .filter(engine => engine.active !== false)
    .map(engine => ({
      value: Number(engine.id),
      label: [engine.bank_name, engine.name, engine.product_type]
        .filter(Boolean)
        .join(' · '),
    }))
);

const selectedCreditEngine = computed(() =>
  creditEngines.value.find(
    engine =>
      Number(engine.id) === Number(selectedNode.value?.action?.creditEngineId)
  )
);
const selectedCreditEngineLabel = computed(() =>
  [selectedCreditEngine.value?.bank_name, selectedCreditEngine.value?.name]
    .filter(Boolean)
    .join(' · ')
);

const inboxOptions = computed(() =>
  (inboxes.value || [])
    .filter(inbox => inbox?.id)
    .map(inbox => ({
      value: Number(inbox.id),
      label: inbox.name || `Caixa ${inbox.id}`,
    }))
);

const parseJsonSample = value => {
  try {
    return JSON.parse(value || '{}');
  } catch {
    return null;
  }
};

const flattenJsonPaths = (value, prefix = '') => {
  if (!value || typeof value !== 'object') return [];

  if (Array.isArray(value)) {
    return value.flatMap((childValue, index) => {
      const path = prefix ? `${prefix}[${index}]` : `items[${index}]`;
      if (childValue && typeof childValue === 'object') {
        return flattenJsonPaths(childValue, path);
      }
      return [path];
    });
  }

  return Object.entries(value).flatMap(([key, childValue]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    if (childValue && typeof childValue === 'object') {
      return flattenJsonPaths(childValue, path);
    }
    return [path];
  });
};

const jsonPathParts = path =>
  String(path || '')
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .filter(Boolean);

const valueFromJsonPath = (payload, path) => {
  if (!payload || !path) return '';
  return (
    jsonPathParts(path).reduce((value, key) => value?.[key], payload) ?? ''
  );
};

const selectedWebhookFields = computed(() => {
  if (selectedNode.value?.type !== 'trigger') return [];
  const payload = parseJsonSample(selectedNode.value.trigger?.webhookSample);
  return flattenJsonPaths(payload).slice(0, 12);
});

const selectedWebhookLastFields = computed(() => {
  const fields = selectedNode.value?.trigger?.lastFields;
  if (!Array.isArray(fields)) return [];
  return fields
    .map(field => (typeof field === 'string' ? { path: field } : field))
    .filter(field => field?.path)
    .slice(0, 16);
});

const selectedWebhookFieldOptions = computed(() =>
  selectedWebhookLastFields.value.length
    ? selectedWebhookLastFields.value.map(field => field.path)
    : selectedWebhookFields.value
);

const selectedWebhookPayloadPreview = computed(() => {
  const payload = selectedNode.value?.trigger?.lastPayload;
  if (!payload) return '';
  return JSON.stringify(payload, null, 2);
});

const sourceNodesFor = node => {
  if (!node) return [];
  return flow.edges
    .filter(edge => edge.target === node.id)
    .map(edge => flow.nodes.find(item => item.id === edge.source))
    .filter(Boolean);
};

const upstreamWebhookTriggerFor = node => {
  const queue = sourceNodesFor(node);
  const visited = new Set();

  while (queue.length) {
    const current = queue.shift();
    if (current && !visited.has(current.id)) {
      visited.add(current.id);

      if (
        current.type === 'trigger' &&
        normalizeNodeTrigger(current.trigger).type === 'webhook_received'
      ) {
        return current;
      }

      queue.push(...sourceNodesFor(current));
    }
  }

  return null;
};

const webhookFieldOptionsFor = triggerNode => {
  if (!triggerNode) return [];
  const fields = triggerNode.trigger?.lastFields;
  if (Array.isArray(fields) && fields.length) {
    return fields
      .map(field => (typeof field === 'string' ? field : field?.path))
      .filter(Boolean)
      .slice(0, 16);
  }

  return flattenJsonPaths(parseJsonSample(triggerNode.trigger?.webhookSample))
    .filter(Boolean)
    .slice(0, 16);
};

const selectedActionWebhookTrigger = computed(() =>
  selectedNode.value?.type === 'trigger'
    ? null
    : upstreamWebhookTriggerFor(selectedNode.value)
);

const selectedActionWebhookFieldOptions = computed(() =>
  webhookFieldOptionsFor(selectedActionWebhookTrigger.value)
);

const filteredNextNodeOptions = computed(() => {
  const query = nodePicker.search.trim().toLowerCase();
  if (!query) return nextNodeOptions;

  return nextNodeOptions.filter(option =>
    [option.title, option.description].some(value =>
      value.toLowerCase().includes(query)
    )
  );
});

const projectOptions = computed(() =>
  funnels.value.map(funnel => ({
    value: funnel.id,
    label: funnel.name || `Projeto ${funnel.id}`,
  }))
);

const nodeCount = computed(() => flow.nodes.length);
const edgeCount = computed(() => flow.edges.length);
const totalLeads = computed(() =>
  flow.nodes.reduce((total, node) => total + Number(node.leads || 0), 0)
);
const averageConversion = computed(() => {
  if (!flow.nodes.length) return 0;
  const total = flow.nodes.reduce(
    (sum, node) => sum + Number(node.conversion || 0),
    0
  );
  return total / flow.nodes.length;
});

const saveStatusLabel = computed(() => {
  if (isLoading.value) return copy.status.loading;
  if (saveError.value) return copy.status.error;
  if (isSaving.value) return copy.status.saving;
  return copy.status.saved;
});

const saveStatusClass = computed(() => {
  if (saveError.value) return 'text-ruby-700';
  if (isSaving.value || isLoading.value) return 'text-amber-700';
  return 'text-emerald-700';
});

const canvasStyle = computed(() => ({
  transform: `translate(${flow.viewport.x}px, ${flow.viewport.y}px) scale(${flow.viewport.zoom})`,
}));

const zoomLabel = computed(() => `${Math.round(flow.viewport.zoom * 100)}%`);

const moneyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const numberFormatter = new Intl.NumberFormat('pt-BR', {
  maximumFractionDigits: 0,
});

const formattedNumber = value => numberFormatter.format(Number(value || 0));

const formattedCurrency = value => moneyFormatter.format(Number(value || 0));

const formattedPercent = value =>
  `${Number(value || 0).toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  })}%`;

const formattedPotential = value => `+${formattedCurrency(value)}`;

const parseInteger = value => {
  const digits = String(value || '').replace(/\D/g, '');
  return Number(digits || 0);
};

const parseDecimal = value => {
  const normalized = String(value || '')
    .replace(/[^\d,.]/g, '')
    .replace(/\./g, '')
    .replace(',', '.');
  return Number(normalized || 0);
};

const parseCurrency = value => {
  const digits = String(value || '').replace(/\D/g, '');
  return Number(digits || 0) / 100;
};

const selectedNodeLeadsDisplay = computed({
  get: () =>
    selectedNode.value ? formattedNumber(selectedNode.value.leads) : '',
  set: value => {
    if (selectedNode.value) selectedNode.value.leads = parseInteger(value);
  },
});

const selectedNodeConversionDisplay = computed({
  get: () =>
    selectedNode.value ? formattedPercent(selectedNode.value.conversion) : '',
  set: value => {
    if (selectedNode.value) selectedNode.value.conversion = parseDecimal(value);
  },
});

const selectedNodeInvestmentDisplay = computed({
  get: () =>
    selectedNode.value ? formattedCurrency(selectedNode.value.investment) : '',
  set: value => {
    if (selectedNode.value)
      selectedNode.value.investment = parseCurrency(value);
  },
});

const selectedNodePotentialDisplay = computed({
  get: () =>
    selectedNode.value ? formattedCurrency(selectedNode.value.potential) : '',
  set: value => {
    if (selectedNode.value) selectedNode.value.potential = parseCurrency(value);
  },
});

const nodeTypeMeta = type =>
  nodeTypes.find(item => item.value === type) || nodeTypes[1];

const nodeColorForType = type => {
  if (type === 'offer') return 'emerald';
  if (type === 'decision') return 'amber';
  return 'blue';
};

const nodeToneClass = color => {
  const tones = {
    emerald: 'border-emerald-300 bg-emerald-50 text-emerald-800',
    blue: 'border-blue-300 bg-blue-50 text-blue-800',
    amber: 'border-amber-300 bg-amber-50 text-amber-800',
    ruby: 'border-ruby-300 bg-ruby-50 text-ruby-800',
    violet: 'border-violet-300 bg-violet-50 text-violet-800',
    slate: 'border-n-weak bg-n-alpha-2 text-n-slate-11',
  };
  return tones[color] || tones.slate;
};

const triggerTypeLabel = type =>
  triggerOptions.find(option => option.value === type)?.label ||
  copy.triggerTypes.conversation_created;

const triggerSummary = node => {
  const trigger = normalizeNodeTrigger(node?.trigger);
  if (trigger.type === 'label_added' && trigger.label) {
    return `${copy.triggerTypes.label_added}: ${trigger.label}`;
  }
  if (trigger.type === 'message_contains' && trigger.keyword) {
    return `${copy.triggerTypes.message_contains}: ${trigger.keyword}`;
  }
  if (trigger.type === 'inbox_changed' && trigger.inboxId) {
    const inbox = inboxOptions.value.find(
      item => Number(item.value) === Number(trigger.inboxId)
    );
    return `${copy.triggerTypes.inbox_changed}: ${inbox?.label || trigger.inboxId}`;
  }
  if (trigger.type === 'webhook_received') {
    return `${copy.triggerTypes.webhook_received}: ${trigger.webhookMethod || 'POST'}`;
  }
  return triggerTypeLabel(trigger.type);
};

const webhookEndpointFor = node => {
  const accountId = route.params.accountId;
  const funnelId = currentFunnelId.value || 'novo';
  const nodeId = node?.id || 'entrada';
  const token = node?.trigger?.webhookToken || 'token';
  const path = `/webhooks/talkvia/funnels/${accountId}/${funnelId}/${nodeId}/${token}`;
  return `${window.location.origin}${path}`;
};

const copyWebhookEndpoint = async node => {
  const endpoint = webhookEndpointFor(node);
  try {
    await copyTextToClipboard(endpoint);
    useAlert(copy.labels.webhookCopied);
  } catch {
    useAlert(copy.labels.webhookCopyFailed);
  }
};

const routeForLinkType = linkType => {
  const accountId = route.params.accountId;
  const routes = {
    conversations: { name: 'talkvia_conversations', params: { accountId } },
    contacts: { name: 'contacts_dashboard_index', params: { accountId } },
    campaigns: { name: 'campaigns_whatsapp_index', params: { accountId } },
    proposals: { name: 'talkvia_proposals', params: { accountId } },
    calendar: { name: 'talkvia_calendar', params: { accountId } },
    integrations: { name: 'settings_applications', params: { accountId } },
  };
  return routes[linkType];
};

const creditEngineLabel = engineId =>
  creditEngineOptions.value.find(
    item => Number(item.value) === Number(engineId)
  )?.label || copy.labels.noCreditEngine;

const openLinkedResource = node => {
  const targetRoute = routeForLinkType(node?.linkType);
  if (targetRoute) router.push(targetRoute);
};

const webhookSummary = action => {
  if (!action.webhookUrl) return copy.actionTypes.call_webhook;
  return `${action.webhookMethod || 'POST'} ${action.webhookUrl}`;
};

const actionSummary = node => {
  const action = normalizeNodeAction(node?.action);
  if (action.type === 'send_message') {
    return action.message || copy.actionTypes.send_message;
  }
  if (action.type === 'send_file') {
    return action.file?.name
      ? `${copy.actionTypes.send_file}: ${action.file.name}`
      : copy.actionTypes.send_file;
  }
  if (action.type === 'add_label') {
    return action.label
      ? `${copy.actionTypes.add_label}: ${action.label}`
      : copy.actionTypes.add_label;
  }
  if (action.type === 'move_inbox') {
    const inbox = inboxOptions.value.find(
      item => Number(item.value) === Number(action.targetInboxId)
    );
    return `${copy.actionTypes.move_inbox}: ${inbox?.label || copy.labels.noInbox}`;
  }
  if (action.type === 'call_webhook') return webhookSummary(action);
  if (action.type === 'run_credit_engine') {
    return `${copy.actionTypes.run_credit_engine}: ${creditEngineLabel(action.creditEngineId)}`;
  }
  return copy.actionTypes[action.type] || copy.actionTypes.none;
};

const nodeExecutionSummary = node =>
  node?.type === 'trigger' ? triggerSummary(node) : actionSummary(node);

const nextNodeFrom = nodeId => {
  const nextEdge = flow.edges.find(edge => edge.source === nodeId);
  return flow.nodes.find(node => node.id === nextEdge?.target);
};

const executionPathFrom = startNode => {
  const path = [];
  const visited = new Set();
  let current = startNode;

  while (current && !visited.has(current.id) && path.length < 24) {
    path.push(current);
    visited.add(current.id);
    current = nextNodeFrom(current.id);
  }

  return path;
};

const clearTestRun = () => {
  testTimers.forEach(timer => window.clearTimeout(timer));
  testTimers = [];
  window.clearTimeout(webhookWaitTimer);
  webhookWaitTimer = null;
  webhookWaitStartedAt = null;
  testRun.activeNodeId = '';
  testRun.isRunning = false;
  testRun.logs = [];
  testRun.statusByNode = {};
};

const closeTestModal = () => {
  testRun.isModalOpen = false;
};

const validationResult = (isValid, message = '') => ({
  isValid,
  message:
    message || (isValid ? copy.labels.testSuccess : copy.labels.testIncomplete),
});

const validateTrigger = node => {
  const trigger = normalizeNodeTrigger(node?.trigger);
  if (trigger.type === 'label_added') {
    return validationResult(Boolean(trigger.label), copy.form.triggerLabel);
  }
  if (trigger.type === 'message_contains') {
    return validationResult(Boolean(trigger.keyword), copy.form.triggerKeyword);
  }
  if (trigger.type === 'inbox_changed') {
    return validationResult(Boolean(trigger.inboxId), copy.form.triggerInbox);
  }
  if (trigger.type === 'webhook_received') {
    const payload = parseJsonSample(trigger.webhookSample);
    if (!payload)
      return validationResult(false, copy.form.triggerWebhookSample);
    const hasAnyPath = [
      trigger.contactPath,
      trigger.phonePath,
      trigger.messagePath,
    ].some(path => valueFromJsonPath(payload, path));
    return validationResult(hasAnyPath, copy.labels.jsonDetectedFields);
  }
  return validationResult(true);
};

const validateAction = node => {
  const action = normalizeNodeAction(node?.action);
  if (action.type === 'send_message') {
    return validationResult(Boolean(action.message), copy.form.actionMessage);
  }
  if (action.type === 'send_file') {
    return validationResult(Boolean(action.file?.name), copy.form.actionFile);
  }
  if (action.type === 'add_label') {
    return validationResult(Boolean(action.label), copy.labels.labelField);
  }
  if (action.type === 'move_inbox') {
    return validationResult(
      Boolean(action.targetInboxId),
      copy.labels.targetInbox
    );
  }
  if (action.type === 'call_webhook') {
    return validationResult(false, copy.labels.outboundWebhookUnavailable);
  }
  if (action.type === 'run_credit_engine') {
    const engine = creditEngines.value.find(
      item => Number(item.id) === Number(action.creditEngineId)
    );
    return validationResult(
      Boolean(action.creditEngineId) &&
        Boolean(engine) &&
        engine.active !== false,
      copy.form.creditEngine
    );
  }
  return validationResult(true);
};

const validateNodeForTest = node =>
  node?.type === 'trigger' ? validateTrigger(node) : validateAction(node);

const pushTestLog = (node, status, validation) => {
  testRun.logs.push({
    id: `${node?.id || 'flow'}-${Date.now()}-${testRun.logs.length}`,
    title: node?.title || flow.name,
    detail: status,
    summary: node ? nodeExecutionSummary(node) : '',
    status: validation?.isValid === false ? 'error' : 'success',
    hint: validation?.isValid === false ? validation.message : '',
  });
};

const runTestFlow = startFromNode => {
  clearTestRun();
  testRun.isModalOpen = true;

  const selectedTrigger =
    selectedNode.value?.type === 'trigger' ? selectedNode.value : null;
  const startNode =
    startFromNode ||
    selectedTrigger ||
    flow.nodes.find(node => node.type === 'trigger');

  if (!startNode) {
    testRun.logs = [
      {
        id: `empty-${Date.now()}`,
        title: copy.labels.testRun,
        detail: copy.labels.noTriggerToTest,
        summary: '',
        status: 'error',
        hint: '',
      },
    ];
    return;
  }

  const path = executionPathFrom(startNode);
  testRun.isRunning = true;

  path.forEach((node, index) => {
    const timer = window.setTimeout(() => {
      if (!testRun.isRunning && index > 0) return;

      testRun.statusByNode = {
        ...testRun.statusByNode,
        [node.id]: 'running',
      };
      const validation = validateNodeForTest(node);
      const nextStatus = validation.isValid ? 'success' : 'error';
      testRun.activeNodeId = node.id;
      testRun.statusByNode = {
        ...testRun.statusByNode,
        [node.id]: nextStatus,
      };
      selectedNodeId.value = node.id;
      selectedEdgeId.value = '';
      pushTestLog(
        node,
        index === 0 ? copy.labels.testStarted : copy.labels.actionSettings,
        validation
      );

      if (index === path.length - 1 || !validation.isValid) {
        testRun.isRunning = false;
        testRun.activeNodeId = '';
        testRun.logs.push({
          id: `done-${Date.now()}`,
          title: validation.isValid
            ? copy.labels.testFinished
            : copy.labels.testError,
          detail: validation.isValid
            ? `${index + 1} passo${index === 0 ? '' : 's'} simulado${
                index === 0 ? '' : 's'
              }`
            : validation.message,
          summary: '',
          status: validation.isValid ? 'success' : 'error',
          hint: '',
        });
      }
    }, index * 550);
    testTimers.push(timer);
  });
};

const nodeCenter = node => ({
  x: node.position.x + NODE_WIDTH / 2,
  y: node.position.y + NODE_HEIGHT / 2,
});

const edgePath = edge => {
  const source = flow.nodes.find(node => node.id === edge.source);
  const target = flow.nodes.find(node => node.id === edge.target);
  if (!source || !target) return '';

  const start = nodeCenter(source);
  const end = nodeCenter(target);
  const offset = Math.max(90, Math.abs(end.x - start.x) / 2);
  const startX = start.x + NODE_WIDTH / 2;
  const endX = end.x - NODE_WIDTH / 2;
  const controlA = startX + offset;
  const controlB = endX - offset;

  return `M ${startX} ${start.y} C ${controlA} ${start.y}, ${controlB} ${end.y}, ${endX} ${end.y}`;
};

const nodeStyle = node => {
  const status = testRun.statusByNode[node.id];
  const baseStyle = {
    left: `${node.position.x}px`,
    top: `${node.position.y}px`,
  };

  if (status === 'running') {
    return {
      ...baseStyle,
      borderColor: '#f59e0b',
      backgroundColor: 'rgba(245, 158, 11, 0.18)',
      boxShadow: '0 0 0 3px rgba(245, 158, 11, 0.28)',
    };
  }

  if (status === 'success') {
    return {
      ...baseStyle,
      borderColor: '#22c55e',
      backgroundColor: 'rgba(34, 197, 94, 0.2)',
      boxShadow: '0 0 0 3px rgba(34, 197, 94, 0.32)',
    };
  }

  if (status === 'error') {
    return {
      ...baseStyle,
      borderColor: '#ef4444',
      backgroundColor: 'rgba(239, 68, 68, 0.2)',
      boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.32)',
    };
  }

  return baseStyle;
};

const testLogStyle = status => {
  if (status === 'error') {
    return {
      borderColor: '#ef4444',
      backgroundColor: 'rgba(239, 68, 68, 0.16)',
      color: '#fecaca',
    };
  }

  return {
    borderColor: '#22c55e',
    backgroundColor: 'rgba(34, 197, 94, 0.16)',
    color: '#bbf7d0',
  };
};

const edgeLabelPosition = edge => {
  const source = flow.nodes.find(node => node.id === edge.source);
  const target = flow.nodes.find(node => node.id === edge.target);
  if (!source || !target) return { x: 0, y: 0 };

  const start = nodeCenter(source);
  const end = nodeCenter(target);
  return { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 - 12 };
};

const legacyStagesFromNodes = () => [
  {
    id: 'visual-flow',
    title: flow.name,
    cards: flow.nodes.map(node => ({
      id: node.id,
      title: node.title,
      description: node.description,
      source: node.source,
      leads: node.leads,
      investment: node.investment,
      conversion: node.conversion,
      potential: node.potential,
      color: node.color,
      linkType: node.linkType,
      metadata: {
        type: node.type,
        position: node.position,
        action: node.action,
        trigger: node.trigger,
      },
    })),
  },
];

const flowPayload = () => ({
  name: flow.name,
  active: automationSettings.active,
  stages: legacyStagesFromNodes(),
  settings: {
    builder: 'talkvia-flow-v1',
    nodes: flow.nodes,
    edges: flow.edges,
    viewport: flow.viewport,
    inbox_id: automationSettings.inboxId || null,
  },
});

const applyFlowState = nextFlow => {
  flow.name = nextFlow.name || 'Funil de Vendas';
  flow.nodes = nextFlow.nodes.map(makeNode);
  flow.edges = nextFlow.edges || [];
  automationSettings.inboxId = nextFlow.inboxId || '';
  automationSettings.active = nextFlow.active !== false;
  flow.viewport = {
    x: Number(nextFlow.viewport?.x ?? 24),
    y: Number(nextFlow.viewport?.y ?? 36),
    zoom: Number(nextFlow.viewport?.zoom ?? 0.85),
  };
};

const flowFromLegacyStages = remoteFlow => {
  const stages = Array.isArray(remoteFlow.stages) ? remoteFlow.stages : [];
  const nodes = stages.flatMap((stage, stageIndex) =>
    (stage.cards || []).map((card, cardIndex) =>
      makeNode({
        ...card,
        type: card.metadata?.type || (stageIndex === 0 ? 'trigger' : 'action'),
        position: card.metadata?.position || {
          x: 90 + stageIndex * 340,
          y: 110 + cardIndex * 190,
        },
      })
    )
  );

  const edges = stages
    .slice(0, -1)
    .flatMap((stage, stageIndex) => {
      const nextStage = stages[stageIndex + 1];
      return (stage.cards || []).map((card, cardIndex) => {
        const target =
          nextStage?.cards?.[Math.min(cardIndex, nextStage.cards.length - 1)];
        return target
          ? {
              id: `edge-${card.id}-${target.id}`,
              source: card.id,
              target: target.id,
              label: nextStage.title,
            }
          : null;
      });
    })
    .filter(Boolean);

  return {
    name: remoteFlow.name,
    nodes: nodes.length ? nodes : defaultFlow().nodes,
    edges: edges.length ? edges : defaultFlow().edges,
    viewport: remoteFlow.settings?.viewport || defaultFlow().viewport,
  };
};

const applyRemoteFlow = remoteFlow => {
  currentFunnelId.value = remoteFlow.id;
  const settings = remoteFlow.settings || {};
  if (settings.builder === 'talkvia-flow-v1' || Array.isArray(settings.nodes)) {
    applyFlowState({
      name: remoteFlow.name,
      nodes: Array.isArray(settings.nodes) ? settings.nodes : [],
      edges: Array.isArray(settings.edges) ? settings.edges : [],
      viewport: settings.viewport,
      inboxId: settings.inbox_id || settings.inboxId || '',
      active: remoteFlow.active !== false,
    });
    return;
  }

  applyFlowState({
    ...flowFromLegacyStages(remoteFlow),
    inboxId: settings.inbox_id || settings.inboxId || '',
    active: remoteFlow.active !== false,
  });
};

const syncCurrentFunnelInList = funnel => {
  const index = funnels.value.findIndex(item => item.id === funnel.id);
  if (index >= 0) {
    funnels.value.splice(index, 1, funnel);
  } else {
    funnels.value.unshift(funnel);
  }
};

const createRemoteFlow = async (name = 'Nova automação') => {
  const fresh = defaultFlow();
  const { data } = await TalkviaFunnelsAPI.create({
    name,
    active: true,
    stages: [],
    settings: {
      builder: 'talkvia-flow-v1',
      nodes: fresh.nodes,
      edges: fresh.edges,
      viewport: fresh.viewport,
      inbox_id: automationSettings.inboxId || null,
    },
  });
  syncCurrentFunnelInList(data);
  applyRemoteFlow(data);
};

const createProject = async () => {
  await createRemoteFlow(`Automação ${funnels.value.length + 1}`);
};

const selectProject = funnelId => {
  const funnel = funnels.value.find(
    item => Number(item.id) === Number(funnelId)
  );
  if (funnel) applyRemoteFlow(funnel);
};

const refreshCurrentFunnel = async () => {
  if (!currentFunnelId.value) return;

  try {
    const { data } = await TalkviaFunnelsAPI.show(currentFunnelId.value);
    syncCurrentFunnelInList(data);
    const previousSelectedNodeId = selectedNodeId.value;
    applyRemoteFlow(data);
    selectedNodeId.value = previousSelectedNodeId;
  } catch {
    saveError.value = true;
  }
};

const webhookPayloadFingerprint = node => {
  const trigger = normalizeNodeTrigger(node?.trigger);
  return JSON.stringify({
    payload: trigger.lastPayload || null,
    receivedAt: trigger.lastReceivedAt || '',
  });
};

const waitForWebhookAndTest = async node => {
  if (!node || node.type !== 'trigger') return;

  clearTestRun();
  testRun.isModalOpen = true;
  testRun.isRunning = true;
  testRun.activeNodeId = node.id;
  testRun.statusByNode = {
    ...testRun.statusByNode,
    [node.id]: 'running',
  };
  testRun.logs = [
    {
      id: `webhook-wait-${Date.now()}`,
      title: copy.labels.webhookWaiting,
      detail: copy.labels.webhookWaitingDetail,
      summary: webhookEndpointFor(node),
      status: 'success',
      hint: '',
    },
  ];

  const initialFingerprint = webhookPayloadFingerprint(node);
  webhookWaitStartedAt = Date.now();

  const poll = async () => {
    if (!testRun.isRunning || testRun.activeNodeId !== node.id) return;

    await refreshCurrentFunnel();
    const refreshedNode = flow.nodes.find(item => item.id === node.id);
    const hasNewPayload =
      webhookPayloadFingerprint(refreshedNode) !== initialFingerprint &&
      refreshedNode?.trigger?.lastPayload;

    if (hasNewPayload) {
      testRun.logs.push({
        id: `webhook-received-${Date.now()}`,
        title: copy.labels.webhookReceived,
        detail: refreshedNode.trigger.lastReceivedAt || copy.labels.testStarted,
        summary: triggerSummary(refreshedNode),
        status: 'success',
        hint: '',
      });
      runTestFlow(refreshedNode);
      return;
    }

    if (Date.now() - webhookWaitStartedAt > 60000) {
      testRun.isRunning = false;
      testRun.activeNodeId = '';
      testRun.statusByNode = {
        ...testRun.statusByNode,
        [node.id]: 'error',
      };
      testRun.logs.push({
        id: `webhook-timeout-${Date.now()}`,
        title: copy.labels.testError,
        detail: copy.labels.webhookWaitTimeout,
        summary: '',
        status: 'error',
        hint: '',
      });
      return;
    }

    webhookWaitTimer = window.setTimeout(poll, 2500);
  };

  webhookWaitTimer = window.setTimeout(poll, 2500);
};

const loadCreditEngines = async () => {
  try {
    const { data } = await TalkviaCreditEnginesAPI.get({ active: true });
    creditEngines.value = data.engines || [];
  } catch {
    creditEngines.value = [];
  }
};

const openCreditEngineSettings = () => {
  router.push({
    name: 'talkvia_credit_engines',
    params: { accountId: route.params.accountId },
  });
};

const loadFlow = async () => {
  isLoading.value = true;
  saveError.value = false;

  try {
    const { data } = await TalkviaFunnelsAPI.get();
    funnels.value = data.funnels || [];
    const [firstFunnel] = funnels.value;

    if (firstFunnel) {
      applyRemoteFlow(firstFunnel);
    } else {
      await createRemoteFlow('Automação principal');
    }
  } catch {
    saveError.value = true;
  } finally {
    isLoading.value = false;
    hasLoadedRemoteFlow.value = true;
  }
};

const saveFlow = async () => {
  if (!hasLoadedRemoteFlow.value || !currentFunnelId.value) return;

  isSaving.value = true;
  saveError.value = false;

  try {
    const { data } = await TalkviaFunnelsAPI.update(
      currentFunnelId.value,
      flowPayload()
    );
    syncCurrentFunnelInList(data);
  } catch {
    saveError.value = true;
  } finally {
    isSaving.value = false;
  }
};

const queueSave = () => {
  window.clearTimeout(saveTimer);
  saveTimer = window.setTimeout(saveFlow, 650);
};

const resetFlow = () => {
  clearTestRun();
  applyFlowState(defaultFlow());
  selectedNodeId.value = '';
  selectedEdgeId.value = '';
};

const addNode = (type, options = {}) => {
  const existingOffset = flow.nodes.length * 28;
  const node = makeNode({
    type,
    title:
      type === 'trigger'
        ? 'Cliente iniciou conversa'
        : copy.nodeTypes[type] || 'Novo nó',
    description: '',
    color: nodeColorForType(type),
    position: {
      x: 180 + existingOffset,
      y: 140 + existingOffset,
    },
    ...options,
  });
  flow.nodes.push(node);
  selectedNodeId.value = node.id;
  selectedEdgeId.value = '';
};

const addTriggerNode = () => addNode('trigger');

const addWebhookTriggerNode = () =>
  addNode('trigger', {
    title: copy.triggerTypes.webhook_received,
    description: 'Recebe JSON externo e libera campos para o próximo passo.',
    color: 'emerald',
    trigger: { type: 'webhook_received', webhookMethod: 'POST' },
  });

const useWebhookOnSelectedTrigger = () => {
  if (!selectedNode.value || selectedNode.value.type !== 'trigger') return;

  selectedNode.value.title = copy.triggerTypes.webhook_received;
  selectedNode.value.color = 'emerald';
  selectedNode.value.trigger = {
    ...normalizeNodeTrigger(selectedNode.value.trigger),
    type: 'webhook_received',
    webhookMethod: selectedNode.value.trigger?.webhookMethod || 'POST',
  };
};

const webhookDefaultsFor = node => {
  const triggerNode =
    node?.type === 'trigger' &&
    normalizeNodeTrigger(node.trigger).type === 'webhook_received'
      ? node
      : upstreamWebhookTriggerFor(node);
  const trigger = normalizeNodeTrigger(triggerNode?.trigger);

  return {
    contactPath: trigger.contactPath || 'cliente.nome',
    phonePath: trigger.phonePath || 'cliente.telefone',
    messagePath: trigger.messagePath || 'mensagem',
  };
};

const defaultActionForType = (actionType, sourceNode) => {
  const webhookDefaults = webhookDefaultsFor(sourceNode);
  const action = {
    ...emptyNodeAction(),
    type: actionType || 'none',
    ...webhookDefaults,
  };

  if (action.type === 'send_message' && webhookDefaults.messagePath) {
    action.message = `{{${webhookDefaults.messagePath}}}`;
  }

  return action;
};

const openNodePicker = node => {
  if (!node) return;
  nodePicker.visible = true;
  nodePicker.sourceNodeId = node.id;
  nodePicker.search = '';
};

const closeNodePicker = () => {
  nodePicker.visible = false;
  nodePicker.sourceNodeId = '';
  nodePicker.search = '';
};

const syncActionDefaults = node => {
  if (!node || node.type === 'trigger') return;
  const action = normalizeNodeAction(node.action);
  const webhookDefaults = webhookDefaultsFor(node);

  node.action = {
    ...action,
    contactPath: action.contactPath || webhookDefaults.contactPath,
    phonePath: action.phonePath || webhookDefaults.phonePath,
    messagePath: action.messagePath || webhookDefaults.messagePath,
  };

  if (
    node.action.type === 'send_message' &&
    !node.action.message &&
    node.action.messagePath
  ) {
    node.action.message = `{{${node.action.messagePath}}}`;
  }
};

const insertActionJsonVariable = field => {
  if (!selectedNode.value || selectedNode.value.type === 'trigger' || !field)
    return;
  const action = normalizeNodeAction(selectedNode.value.action);
  selectedNode.value.action = {
    ...action,
    message: `${action.message || ''}{{${field}}}`,
  };
};

const addConnectedNodeAfter = (node, type = 'action', options = {}) => {
  if (!node) return;

  const actionType = options.actionType || 'none';
  const nextNode = makeNode({
    type,
    title:
      options.title ||
      (type === 'action' ? copy.nodeTypes.action : copy.nodeTypes[type]),
    description: options.description || '',
    color: options.color || nodeColorForType(type),
    action:
      type === 'action'
        ? defaultActionForType(actionType, node)
        : emptyNodeAction(),
    position: {
      x: node.position.x + NODE_WIDTH + 140,
      y: node.position.y,
    },
  });

  flow.nodes.push(nextNode);
  flow.edges.push({
    id: `edge-${node.id}-${nextNode.id}-${Date.now()}`,
    source: node.id,
    target: nextNode.id,
    label: actionType === 'none' ? '' : copy.actionTypes[actionType] || '',
  });
  selectedNodeId.value = nextNode.id;
  selectedEdgeId.value = '';
  closeNodePicker();
  isNodeInspectorOpen.value = true;
};

const addNodeFromPicker = option => {
  const sourceNode = flow.nodes.find(
    node => node.id === nodePicker.sourceNodeId
  );
  addConnectedNodeAfter(sourceNode, option.type, option);
};

const closeContextMenu = () => {
  contextMenu.visible = false;
  contextMenu.nodeId = '';
};

const duplicateNode = node => {
  if (!node) return;
  const copyNode = makeNode({
    ...node,
    id: undefined,
    title: `${node.title} cópia`,
    position: {
      x: node.position.x + 36,
      y: node.position.y + 36,
    },
  });
  flow.nodes.push(copyNode);
  selectedNodeId.value = copyNode.id;
  selectedEdgeId.value = '';
};

const removeNode = nodeId => {
  flow.nodes = flow.nodes.filter(node => node.id !== nodeId);
  flow.edges = flow.edges.filter(
    edge => edge.source !== nodeId && edge.target !== nodeId
  );
  selectedNodeId.value = '';
  closeContextMenu();
};

const handleActionFileUpload = async event => {
  const [file] = Array.from(event.target.files || []);
  if (!file || !selectedNode.value) return;

  isUploadingFile.value = true;
  uploadError.value = false;
  try {
    const uploaded = await uploadFile(file, route.params.accountId);
    selectedNode.value.action = {
      ...normalizeNodeAction(selectedNode.value.action),
      type: 'send_file',
      file: {
        name: file.name,
        size: file.size,
        type: file.type,
        fileUrl: uploaded.fileUrl,
        blobId: uploaded.blobId,
        blobKey: uploaded.blobKey,
      },
    };
  } catch {
    uploadError.value = true;
  } finally {
    isUploadingFile.value = false;
    event.target.value = '';
  }
};

const removeEdge = edgeId => {
  flow.edges = flow.edges.filter(edge => edge.id !== edgeId);
  selectedEdgeId.value = '';
};

const removeSelected = () => {
  if (selectedNodeId.value) {
    removeNode(selectedNodeId.value);
    return;
  }

  if (selectedEdgeId.value) removeEdge(selectedEdgeId.value);
};

const createEdge = (sourceId, targetId) => {
  if (!sourceId || !targetId || sourceId === targetId) return;

  const exists = flow.edges.some(
    edge => edge.source === sourceId && edge.target === targetId
  );
  if (!exists) {
    flow.edges.push({
      id: `edge-${sourceId}-${targetId}-${Date.now()}`,
      source: sourceId,
      target: targetId,
      label: '',
    });
  }

  isConnecting.value = false;
  connectionSourceId.value = '';
};

const startConnection = nodeId => {
  closeContextMenu();
  connectionSourceId.value = nodeId;
  isConnecting.value = true;
  selectedNodeId.value = nodeId;
  selectedEdgeId.value = '';
};

const finishConnection = nodeId => {
  if (!connectionSourceId.value) return;
  createEdge(connectionSourceId.value, nodeId);
};

const openNodeInspector = nodeId => {
  if (!nodeId) return;
  closeContextMenu();
  selectedNodeId.value = nodeId;
  selectedEdgeId.value = '';
  isNodeInspectorOpen.value = true;
};

const closeNodeInspector = () => {
  isNodeInspectorOpen.value = false;
};

const selectNode = (nodeId, options = {}) => {
  closeContextMenu();
  if (isConnecting.value) {
    if (!connectionSourceId.value) {
      startConnection(nodeId);
      return;
    }

    createEdge(connectionSourceId.value, nodeId);
    return;
  }

  selectedNodeId.value = nodeId;
  selectedEdgeId.value = '';

  if (options.openInspector) {
    isNodeInspectorOpen.value = true;
  }
};

const selectEdge = edgeId => {
  closeContextMenu();
  selectedEdgeId.value = edgeId;
  selectedNodeId.value = '';
};

const toggleConnectMode = () => {
  isConnecting.value = !isConnecting.value;
  connectionSourceId.value = '';
};

const connectionStatusLabel = computed(() =>
  connectionSourceId.value ? copy.labels.connectActive : copy.labels.connectIdle
);

const zoomBy = amount => {
  flow.viewport.zoom = Math.min(
    MAX_ZOOM,
    Math.max(MIN_ZOOM, flow.viewport.zoom + amount)
  );
};

const fitView = () => {
  flow.viewport.x = 24;
  flow.viewport.y = 36;
  flow.viewport.zoom = 0.85;
};

const openNodeContextMenu = (event, node) => {
  event.preventDefault();
  event.stopPropagation();
  selectedNodeId.value = node.id;
  selectedEdgeId.value = '';
  contextMenu.visible = true;
  contextMenu.nodeId = node.id;
  contextMenu.x = event.clientX;
  contextMenu.y = event.clientY;
};

const runContextAction = action => {
  const node = contextMenuNode.value;
  if (!node) {
    closeContextMenu();
    return;
  }

  if (action === 'edit') openNodeInspector(node.id);
  if (action === 'duplicate') duplicateNode(node);
  if (action === 'connect') startConnection(node.id);
  if (action === 'open') openLinkedResource(node);
  if (action === 'remove') removeNode(node.id);
  closeContextMenu();
};

const isTypingTarget = target => {
  const tagName = target?.tagName?.toLowerCase();
  return (
    target?.isContentEditable ||
    ['input', 'textarea', 'select'].includes(tagName)
  );
};

const onKeyDown = event => {
  if (isTypingTarget(event.target)) return;

  if (event.key === 'Escape') {
    closeNodeInspector();
    closeContextMenu();
    isConnecting.value = false;
    connectionSourceId.value = '';
    return;
  }

  if (event.key === 'Delete' || event.key === 'Backspace') {
    if (!selectedNodeId.value && !selectedEdgeId.value) return;
    event.preventDefault();
    removeSelected();
  }
};

const canvasPointFromEvent = event => {
  const rect = canvasEl.value?.getBoundingClientRect();
  return {
    x:
      (event.clientX - (rect?.left || 0) - flow.viewport.x) /
      flow.viewport.zoom,
    y:
      (event.clientY - (rect?.top || 0) - flow.viewport.y) / flow.viewport.zoom,
  };
};

const startNodeDrag = (event, node) => {
  if (isConnecting.value || event.target.closest('[data-node-action]')) return;
  const point = canvasPointFromEvent(event);
  dragState.value = {
    nodeId: node.id,
    offsetX: point.x - node.position.x,
    offsetY: point.y - node.position.y,
  };
  selectNode(node.id, { openInspector: false });
};

const startPan = event => {
  closeContextMenu();
  if (event.target.closest('[data-flow-node]')) return;
  panState.value = {
    startX: event.clientX,
    startY: event.clientY,
    originX: flow.viewport.x,
    originY: flow.viewport.y,
  };
  selectedNodeId.value = '';
  selectedEdgeId.value = '';
};

const onPointerMove = event => {
  if (dragState.value) {
    const point = canvasPointFromEvent(event);
    const node = flow.nodes.find(item => item.id === dragState.value.nodeId);
    if (node) {
      node.position.x = Math.round(point.x - dragState.value.offsetX);
      node.position.y = Math.round(point.y - dragState.value.offsetY);
    }
    return;
  }

  if (panState.value) {
    flow.viewport.x =
      panState.value.originX + event.clientX - panState.value.startX;
    flow.viewport.y =
      panState.value.originY + event.clientY - panState.value.startY;
  }
};

const stopPointerInteraction = () => {
  dragState.value = null;
  panState.value = null;
};

const onCanvasWheel = event => {
  event.preventDefault();
  const nextZoom = Math.min(
    MAX_ZOOM,
    Math.max(MIN_ZOOM, flow.viewport.zoom + (event.deltaY > 0 ? -0.08 : 0.08))
  );
  flow.viewport.zoom = Number(nextZoom.toFixed(2));
};

watch(flow, queueSave, { deep: true });
watch(automationSettings, queueSave, { deep: true });

onMounted(async () => {
  await Promise.all([
    store.dispatch('inboxes/getFromNetwork'),
    loadFlow(),
    loadCreditEngines(),
  ]);
  await nextTick();
  window.addEventListener('pointermove', onPointerMove);
  window.addEventListener('pointerup', stopPointerInteraction);
  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('click', closeContextMenu);
});

onBeforeUnmount(() => {
  window.clearTimeout(saveTimer);
  clearTestRun();
  window.removeEventListener('pointermove', onPointerMove);
  window.removeEventListener('pointerup', stopPointerInteraction);
  window.removeEventListener('keydown', onKeyDown);
  window.removeEventListener('click', closeContextMenu);
});
</script>

<template>
  <main class="min-h-full min-w-0 flex-1 bg-n-background text-n-slate-12">
    <section class="flex h-[calc(100vh-64px)] min-h-[720px] flex-col">
      <header
        class="flex shrink-0 flex-col gap-4 border-b border-n-weak bg-n-solid-1 px-4 py-4 lg:flex-row lg:items-center lg:justify-between lg:px-6"
      >
        <div class="min-w-0">
          <div
            class="mb-2 flex items-center gap-2 text-xs font-medium uppercase text-n-slate-10"
          >
            <span class="i-lucide-workflow size-3.5" />
            {{ copy.eyebrow }}
          </div>
          <div class="flex flex-wrap items-center gap-3">
            <input
              v-model="flow.name"
              class="min-w-64 rounded-lg border border-transparent bg-transparent px-0 text-xl font-semibold outline-none focus:border-n-brand focus:bg-n-alpha-2 focus:px-3 md:text-2xl"
              :aria-label="copy.form.name"
            />
            <span
              class="inline-flex h-8 items-center gap-2 rounded-lg border border-n-weak bg-n-solid-2 px-3 text-xs font-medium"
              :class="saveStatusClass"
            >
              <span class="i-lucide-cloud size-4" />
              {{ saveStatusLabel }}
            </span>
          </div>
          <div class="mt-3 flex flex-wrap items-center gap-2">
            <label
              class="flex min-w-56 flex-col gap-1 text-xs font-medium text-n-slate-10"
            >
              {{ copy.form.project }}
              <div class="relative">
                <select
                  :value="currentFunnelId"
                  :class="selectClass"
                  @change="selectProject($event.target.value)"
                >
                  <option
                    v-for="project in projectOptions"
                    :key="project.value"
                    :value="project.value"
                  >
                    {{ project.label }}
                  </option>
                </select>
                <div
                  class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
                >
                  <span class="i-lucide-chevron-down size-4 text-n-slate-11" />
                </div>
              </div>
            </label>
            <label
              class="flex min-w-40 flex-col gap-1 text-xs font-medium text-n-slate-10"
            >
              {{ copy.form.status }}
              <div class="relative">
                <select
                  v-model="automationSettings.active"
                  :class="selectClass"
                >
                  <option :value="true">{{ copy.labels.active }}</option>
                  <option :value="false">{{ copy.labels.inactive }}</option>
                </select>
                <div
                  class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
                >
                  <span class="i-lucide-chevron-down size-4 text-n-slate-11" />
                </div>
              </div>
            </label>
            <label
              class="flex min-w-56 flex-col gap-1 text-xs font-medium text-n-slate-10"
            >
              {{ copy.form.inbox }}
              <div class="relative">
                <select
                  v-model="automationSettings.inboxId"
                  :class="selectClass"
                >
                  <option value="">{{ copy.labels.noInbox }}</option>
                  <option
                    v-for="inbox in inboxOptions"
                    :key="inbox.value"
                    :value="inbox.value"
                  >
                    {{ inbox.label }}
                  </option>
                </select>
                <div
                  class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
                >
                  <span class="i-lucide-chevron-down size-4 text-n-slate-11" />
                </div>
              </div>
            </label>
          </div>
          <p class="mt-2 max-w-3xl text-sm text-n-slate-11">
            {{ copy.subtitle }}
          </p>
        </div>

        <div class="flex flex-wrap items-center gap-2">
          <button
            type="button"
            class="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-n-brand px-3 text-sm font-medium text-white hover:brightness-95"
            @click="createProject"
          >
            <span class="i-lucide-folder-plus size-4" />
            {{ copy.actions.newProject }}
          </button>
          <button
            type="button"
            class="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-n-weak px-3 text-sm font-medium hover:bg-n-alpha-2"
            @click="resetFlow"
          >
            <span class="i-lucide-refresh-cw size-4" />
            {{ copy.actions.reset }}
          </button>
          <button
            type="button"
            class="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-n-weak px-3 text-sm font-medium hover:bg-n-alpha-2"
            @click="fitView"
          >
            <span class="i-lucide-scan size-4" />
            {{ copy.actions.fit }}
          </button>
          <button
            type="button"
            class="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-3 text-sm font-medium text-emerald-800 hover:bg-emerald-100"
            @click="runTestFlow"
          >
            <span class="i-lucide-play-circle size-4" />
            {{ copy.actions.testFlow }}
          </button>
          <button
            type="button"
            class="inline-flex h-9 items-center justify-center gap-2 rounded-lg px-3 text-sm font-medium"
            :class="
              isConnecting
                ? 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                : 'border border-n-weak hover:bg-n-alpha-2'
            "
            @click="toggleConnectMode"
          >
            <span class="i-lucide-git-compare-arrows size-4" />
            {{
              isConnecting ? copy.actions.cancelConnect : copy.actions.connect
            }}
          </button>
        </div>
      </header>

      <div
        class="grid min-h-0 flex-1 grid-cols-1"
        :class="
          isFlowPanelCollapsed
            ? 'lg:grid-cols-[1fr_56px]'
            : 'lg:grid-cols-[1fr_340px]'
        "
      >
        <section class="relative min-w-0 overflow-hidden bg-n-background">
          <div class="absolute left-4 top-4 z-20 grid gap-2">
            <div
              class="flex overflow-hidden rounded-lg border border-n-weak bg-n-solid-1 shadow-sm"
            >
              <button
                type="button"
                class="grid size-9 place-content-center hover:bg-n-alpha-2"
                @click="zoomBy(0.1)"
              >
                <span class="i-lucide-plus size-4" />
              </button>
              <span
                class="grid h-9 min-w-14 place-content-center border-x border-n-weak text-xs font-medium"
              >
                {{ zoomLabel }}
              </span>
              <button
                type="button"
                class="grid size-9 place-content-center hover:bg-n-alpha-2"
                @click="zoomBy(-0.1)"
              >
                <span class="i-lucide-minus size-4" />
              </button>
            </div>
          </div>

          <div
            v-if="isConnecting"
            class="absolute left-4 top-16 z-20 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800 shadow-sm"
          >
            {{ connectionStatusLabel }}
          </div>

          <div class="absolute bottom-4 left-4 z-20 grid gap-2 sm:grid-cols-4">
            <article
              class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 shadow-sm"
            >
              <p class="text-[11px] uppercase text-n-slate-10">
                {{ copy.metrics.nodes }}
              </p>
              <p class="text-lg font-semibold">{{ nodeCount }}</p>
            </article>
            <article
              class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 shadow-sm"
            >
              <p class="text-[11px] uppercase text-n-slate-10">
                {{ copy.metrics.edges }}
              </p>
              <p class="text-lg font-semibold">{{ edgeCount }}</p>
            </article>
            <article
              class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 shadow-sm"
            >
              <p class="text-[11px] uppercase text-n-slate-10">
                {{ copy.metrics.leads }}
              </p>
              <p class="text-lg font-semibold">
                {{ formattedNumber(totalLeads) }}
              </p>
            </article>
            <article
              class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 shadow-sm"
            >
              <p class="text-[11px] uppercase text-n-slate-10">
                {{ copy.metrics.conversion }}
              </p>
              <p class="text-lg font-semibold">
                {{ formattedPercent(averageConversion) }}
              </p>
            </article>
          </div>

          <div
            ref="canvasEl"
            class="h-full w-full cursor-grab overflow-hidden bg-[radial-gradient(circle_at_1px_1px,rgba(148,163,184,0.28)_1px,transparent_0)] [background-size:24px_24px] active:cursor-grabbing"
            @pointerdown="startPan"
            @wheel="onCanvasWheel"
          >
            <div
              class="relative h-[1800px] w-[2200px] origin-top-left"
              :style="canvasStyle"
            >
              <svg
                class="pointer-events-none absolute inset-0 h-full w-full"
                aria-hidden="true"
              >
                <defs>
                  <marker
                    id="flow-arrow-dot"
                    markerHeight="6"
                    markerWidth="6"
                    orient="auto"
                    refX="3"
                    refY="3"
                  >
                    <circle cx="3" cy="3" r="1.8" class="fill-n-brand" />
                  </marker>
                </defs>
                <path
                  v-for="edge in flow.edges"
                  :key="edge.id"
                  :d="edgePath(edge)"
                  fill="none"
                  marker-end="url(#flow-arrow-dot)"
                  stroke="currentColor"
                  stroke-dasharray="1 10"
                  stroke-linecap="round"
                  stroke-width="3"
                  class="pointer-events-auto cursor-pointer text-n-slate-8 hover:text-n-brand"
                  :class="selectedEdgeId === edge.id ? 'text-n-brand' : ''"
                  @click.stop="selectEdge(edge.id)"
                >
                  <animate
                    attributeName="stroke-dashoffset"
                    dur="1s"
                    from="11"
                    repeatCount="indefinite"
                    to="0"
                  />
                </path>
              </svg>

              <button
                v-for="edge in flow.edges"
                :key="`${edge.id}-label`"
                type="button"
                class="absolute z-10 max-w-40 -translate-x-1/2 rounded-full border border-n-weak bg-n-solid-1 px-2 py-1 text-xs font-medium text-n-slate-10 shadow-sm hover:text-n-slate-12"
                :style="{
                  left: `${edgeLabelPosition(edge).x}px`,
                  top: `${edgeLabelPosition(edge).y}px`,
                }"
                @click.stop="selectEdge(edge.id)"
              >
                {{ edge.label || copy.labels.edgeDefault }}
              </button>

              <article
                v-for="node in flow.nodes"
                :key="node.id"
                data-flow-node
                class="absolute z-20 flex h-[206px] w-[320px] cursor-grab flex-col rounded-lg border bg-n-solid-1 p-4 shadow-sm transition hover:shadow-md active:cursor-grabbing"
                :class="[
                  selectedNodeId === node.id
                    ? 'border-n-brand ring-2 ring-n-brand/20'
                    : 'border-n-weak',
                  connectionSourceId === node.id ? 'ring-2 ring-amber-300' : '',
                  testRun.activeNodeId === node.id
                    ? 'ring-2 ring-emerald-400 shadow-emerald-500/20'
                    : '',
                  testRun.statusByNode[node.id] === 'running'
                    ? 'border-amber-300 bg-amber-50/80 text-amber-950 dark:bg-amber-950/20 dark:text-amber-100'
                    : '',
                  testRun.statusByNode[node.id] === 'success'
                    ? 'border-emerald-400 bg-emerald-50/80 text-emerald-950 dark:bg-emerald-950/20 dark:text-emerald-100'
                    : '',
                  testRun.statusByNode[node.id] === 'error'
                    ? 'border-ruby-400 bg-ruby-50/80 text-ruby-950 dark:bg-ruby-950/20 dark:text-ruby-100'
                    : '',
                ]"
                :style="nodeStyle(node)"
                @pointerdown.stop="startNodeDrag($event, node)"
                @click.stop="selectNode(node.id)"
                @dblclick.stop="openNodeInspector(node.id)"
                @contextmenu="openNodeContextMenu($event, node)"
              >
                <button
                  type="button"
                  data-node-action
                  class="absolute -left-3 top-1/2 grid size-6 -translate-y-1/2 place-content-center rounded-full border border-n-weak bg-n-solid-1 text-n-slate-9 shadow-sm transition hover:border-n-brand hover:text-n-brand disabled:opacity-30"
                  :title="copy.labels.connectorEndTitle"
                  :aria-label="copy.labels.connectorEndTitle"
                  :disabled="
                    !connectionSourceId || connectionSourceId === node.id
                  "
                  @pointerdown.stop
                  @click.stop="finishConnection(node.id)"
                >
                  <span class="i-lucide-dot size-4" />
                </button>
                <button
                  type="button"
                  data-node-action
                  class="absolute -right-3 top-1/2 grid size-6 -translate-y-1/2 place-content-center rounded-full border border-n-weak bg-n-solid-1 text-n-slate-9 shadow-sm transition hover:border-n-brand hover:text-n-brand"
                  :title="copy.labels.connectorStartTitle"
                  :aria-label="copy.labels.connectorStartTitle"
                  @pointerdown.stop
                  @click.stop="openNodePicker(node)"
                >
                  <span class="i-lucide-plus size-3.5" />
                </button>
                <div class="flex items-start justify-between gap-3">
                  <div class="min-w-0">
                    <div
                      class="mb-2 inline-flex max-w-full items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium"
                      :class="nodeToneClass(node.color)"
                    >
                      <span
                        class="size-3.5"
                        :class="[nodeTypeMeta(node.type).icon]"
                      />
                      <span class="truncate">{{
                        nodeTypeMeta(node.type).label
                      }}</span>
                    </div>
                    <h2 class="line-clamp-2 text-sm font-semibold leading-5">
                      {{ node.title }}
                    </h2>
                    <p
                      v-if="node.type === 'trigger'"
                      class="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700"
                    >
                      <span class="i-lucide-play-circle size-3" />
                      {{ copy.labels.flowStart }}
                    </p>
                    <p
                      v-if="testRun.statusByNode[node.id]"
                      class="mt-1 inline-flex items-center gap-1 text-[11px] font-medium"
                      :class="
                        testRun.statusByNode[node.id] === 'running'
                          ? 'text-amber-700 dark:text-amber-300'
                          : testRun.statusByNode[node.id] === 'success'
                            ? 'text-emerald-700 dark:text-emerald-300'
                            : 'text-ruby-700 dark:text-ruby-300'
                      "
                    >
                      <span
                        class="size-3"
                        :class="
                          testRun.statusByNode[node.id] === 'running'
                            ? 'i-lucide-loader-circle animate-spin'
                            : testRun.statusByNode[node.id] === 'success'
                              ? 'i-lucide-check-circle-2'
                              : 'i-lucide-x-circle'
                        "
                      />
                      {{
                        testRun.statusByNode[node.id] === 'running'
                          ? copy.labels.testRunning
                          : testRun.statusByNode[node.id] === 'success'
                            ? copy.labels.testSuccess
                            : copy.labels.testError
                      }}
                    </p>
                  </div>
                  <div class="flex shrink-0 items-center gap-1">
                    <button
                      type="button"
                      data-node-action
                      class="grid size-7 place-content-center rounded-md text-n-slate-9 transition hover:bg-n-alpha-2 hover:text-n-brand"
                      :title="copy.actions.editNode"
                      :aria-label="copy.actions.editNode"
                      @pointerdown.stop
                      @click.stop="openNodeInspector(node.id)"
                    >
                      <span class="i-lucide-panel-right-open size-4" />
                    </button>
                    <span class="i-lucide-grip size-4 text-n-slate-9" />
                  </div>
                </div>
                <p
                  class="mt-2 line-clamp-2 min-h-9 text-xs leading-5 text-n-slate-11"
                >
                  {{
                    node.type === 'trigger'
                      ? triggerSummary(node)
                      : node.description || actionSummary(node) || node.source
                  }}
                </p>
                <div class="mt-auto grid grid-cols-3 gap-2 text-xs">
                  <div class="min-w-0 rounded-md bg-n-alpha-2 px-2 py-1">
                    <p class="text-n-slate-10">{{ copy.metrics.leads }}</p>
                    <p class="truncate font-semibold">
                      {{ formattedNumber(node.leads) }}
                    </p>
                  </div>
                  <div class="min-w-0 rounded-md bg-n-alpha-2 px-2 py-1">
                    <p class="text-n-slate-10">
                      {{ copy.labels.conversionShort }}
                    </p>
                    <p class="truncate font-semibold">
                      {{ formattedPercent(node.conversion) }}
                    </p>
                  </div>
                  <div
                    class="min-w-0 rounded-md bg-emerald-50 px-2 py-1 text-emerald-700"
                  >
                    <p>{{ copy.labels.potentialShort }}</p>
                    <p class="truncate font-semibold">
                      {{ formattedPotential(node.potential) }}
                    </p>
                  </div>
                </div>
              </article>
            </div>
          </div>

          <div
            v-if="contextMenu.visible"
            class="fixed z-50 w-52 overflow-hidden rounded-lg border border-n-weak bg-n-solid-1 py-1 text-sm shadow-lg"
            :style="{ left: `${contextMenu.x}px`, top: `${contextMenu.y}px` }"
            @click.stop
          >
            <button
              type="button"
              class="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-n-alpha-2"
              @click="runContextAction('edit')"
            >
              <span class="i-lucide-panel-right-open size-4" />
              {{ copy.actions.editNode }}
            </button>
            <button
              type="button"
              class="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-n-alpha-2"
              @click="runContextAction('connect')"
            >
              <span class="i-lucide-git-compare-arrows size-4" />
              {{ copy.actions.connect }}
            </button>
            <button
              type="button"
              class="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-n-alpha-2"
              @click="runContextAction('duplicate')"
            >
              <span class="i-lucide-copy size-4" />
              {{ copy.actions.duplicate }}
            </button>
            <button
              type="button"
              class="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-n-alpha-2"
              @click="runContextAction('open')"
            >
              <span class="i-lucide-external-link size-4" />
              {{ copy.actions.openLink }}
            </button>
            <button
              type="button"
              class="flex w-full items-center gap-2 px-3 py-2 text-left text-ruby-700 hover:bg-ruby-50"
              @click="runContextAction('remove')"
            >
              <span class="i-lucide-trash-2 size-4" />
              {{ copy.actions.remove }}
            </button>
          </div>
        </section>

        <aside
          class="flow-side-panel flex min-h-0 flex-col border-l border-n-weak bg-n-solid-1"
          :class="{ 'is-collapsed': isFlowPanelCollapsed }"
        >
          <div class="flow-start-panel border-b border-n-weak p-4">
            <button
              type="button"
              class="mb-3 hidden size-9 place-content-center rounded-lg border border-n-weak text-n-slate-11 hover:bg-n-alpha-2 lg:grid"
              :title="
                isFlowPanelCollapsed
                  ? copy.actions.expandPanel
                  : copy.actions.collapsePanel
              "
              :aria-label="
                isFlowPanelCollapsed
                  ? copy.actions.expandPanel
                  : copy.actions.collapsePanel
              "
              @click="isFlowPanelCollapsed = !isFlowPanelCollapsed"
            >
              <span
                class="size-4"
                :class="
                  isFlowPanelCollapsed
                    ? 'i-lucide-panel-right-open'
                    : 'i-lucide-panel-right-close'
                "
              />
            </button>
            <p class="text-sm font-semibold">{{ copy.labels.flowStart }}</p>
            <p class="mt-1 text-xs leading-5 text-n-slate-10">
              {{ copy.labels.flowStartDescription }}
            </p>
            <div class="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-1">
              <button
                type="button"
                class="inline-flex h-9 w-full items-center justify-center gap-2 rounded-lg bg-n-brand px-3 text-sm font-medium text-white hover:brightness-95"
                @click="addTriggerNode"
              >
                <span class="i-lucide-play size-4" />
                {{ copy.actions.addTrigger }}
              </button>
              <button
                type="button"
                class="inline-flex h-9 w-full items-center justify-center gap-2 rounded-lg border border-emerald-500/50 bg-emerald-500/15 px-3 text-sm font-medium text-emerald-200 hover:bg-emerald-500/25"
                @click="addWebhookTriggerNode"
              >
                <span class="i-lucide-radio-tower size-4" />
                {{ copy.actions.addWebhookTrigger }}
              </button>
            </div>
            <p class="mt-4 text-sm font-semibold">{{ copy.actions.addNode }}</p>
            <div
              class="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-2"
            >
              <button
                v-for="type in nodeTypes"
                :key="type.value"
                type="button"
                class="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-n-weak px-2 text-xs font-medium hover:border-n-brand hover:text-n-brand"
                @click="addNode(type.value)"
              >
                <span class="size-4" :class="[type.icon]" />
                {{ type.label }}
              </button>
            </div>
          </div>
        </aside>
      </div>

      <div v-if="selectedNode && isNodeInspectorOpen" class="node-editor-modal">
        <div class="node-editor-backdrop" @click="closeNodeInspector" />
        <section class="node-editor-dialog">
          <div class="min-h-0 flex-1 overflow-y-auto p-4 lg:p-5">
            <div v-if="selectedNode" class="grid gap-4">
              <div class="flex items-center justify-between gap-3">
                <p class="text-sm font-semibold">{{ copy.actions.editNode }}</p>
                <button
                  type="button"
                  class="grid size-9 place-content-center rounded-lg border border-n-weak hover:bg-n-alpha-2"
                  :aria-label="copy.actions.close"
                  @click="closeNodeInspector"
                >
                  <span class="i-lucide-x size-4" />
                </button>
              </div>

              <section class="node-flow-summary">
                <div>
                  <span class="i-lucide-log-in size-4" />
                  <p>{{ copy.labels.inputColumn }}</p>
                  <strong>{{
                    selectedNode.type === 'trigger'
                      ? triggerSummary(selectedNode)
                      : selectedNode.source || copy.labels.noInbox
                  }}</strong>
                </div>
                <div>
                  <span class="i-lucide-workflow size-4" />
                  <p>{{ copy.labels.processColumn }}</p>
                  <strong>{{ nodeTypeMeta(selectedNode.type).label }}</strong>
                </div>
                <div>
                  <span class="i-lucide-log-out size-4" />
                  <p>{{ copy.labels.outputColumn }}</p>
                  <strong>{{
                    selectedNode.type === 'trigger'
                      ? copy.actions.addNextAction
                      : actionSummary(selectedNode)
                  }}</strong>
                </div>
              </section>
              <div class="flex items-center justify-between gap-3">
                <div>
                  <p class="text-xs uppercase text-n-slate-10">
                    {{ copy.labels.selectedNode }}
                  </p>
                  <p class="truncate font-semibold">{{ selectedNode.title }}</p>
                </div>
                <button
                  type="button"
                  class="grid size-9 place-content-center rounded-lg border border-n-weak text-ruby-700 hover:bg-ruby-50"
                  :aria-label="copy.actions.remove"
                  @click="removeNode(selectedNode.id)"
                >
                  <span class="i-lucide-trash-2 size-4" />
                </button>
              </div>

              <label class="grid gap-2 text-sm font-medium">
                {{ copy.form.nodeTitle }}
                <input v-model="selectedNode.title" :class="inputClass" />
              </label>

              <label class="grid gap-2 text-sm font-medium">
                {{ copy.form.description }}
                <textarea
                  v-model="selectedNode.description"
                  rows="3"
                  class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm outline-none focus:border-n-brand"
                />
              </label>

              <div class="grid gap-3 sm:grid-cols-2">
                <label class="grid gap-2 text-sm font-medium">
                  {{ copy.form.nodeType }}
                  <div class="relative">
                    <select v-model="selectedNode.type" :class="selectClass">
                      <option
                        v-for="type in nodeTypes"
                        :key="type.value"
                        :value="type.value"
                      >
                        {{ type.label }}
                      </option>
                    </select>
                    <div
                      class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
                    >
                      <span
                        class="i-lucide-chevron-down size-4 text-n-slate-11"
                      />
                    </div>
                  </div>
                </label>
                <label class="grid gap-2 text-sm font-medium">
                  {{ copy.form.color }}
                  <div class="relative">
                    <select v-model="selectedNode.color" :class="selectClass">
                      <option
                        v-for="tone in cardPalette"
                        :key="tone.value"
                        :value="tone.value"
                      >
                        {{ tone.label }}
                      </option>
                    </select>
                    <div
                      class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
                    >
                      <span
                        class="i-lucide-chevron-down size-4 text-n-slate-11"
                      />
                    </div>
                  </div>
                </label>
              </div>

              <label class="grid gap-2 text-sm font-medium">
                {{ copy.form.source }}
                <input v-model="selectedNode.source" :class="inputClass" />
              </label>

              <label class="grid gap-2 text-sm font-medium">
                {{ copy.form.linkType }}
                <div class="relative">
                  <select v-model="selectedNode.linkType" :class="selectClass">
                    <option
                      v-for="option in linkOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                  <div
                    class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
                  >
                    <span
                      class="i-lucide-chevron-down size-4 text-n-slate-11"
                    />
                  </div>
                </div>
              </label>

              <section
                v-if="selectedNode.type === 'trigger'"
                class="grid gap-3 rounded-lg border border-n-weak bg-n-alpha-1 p-3"
              >
                <p class="text-sm font-semibold">
                  {{ copy.labels.triggerSettings }}
                </p>

                <label class="grid gap-2 text-sm font-medium">
                  {{ copy.form.triggerType }}
                  <div class="relative">
                    <select
                      v-model="selectedNode.trigger.type"
                      :class="selectClass"
                    >
                      <option
                        v-for="trigger in triggerOptions"
                        :key="trigger.value"
                        :value="trigger.value"
                      >
                        {{ trigger.label }}
                      </option>
                    </select>
                    <div
                      class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
                    >
                      <span
                        class="i-lucide-chevron-down size-4 text-n-slate-11"
                      />
                    </div>
                  </div>
                </label>

                <label
                  v-if="selectedNode.trigger.type === 'label_added'"
                  class="grid gap-2 text-sm font-medium"
                >
                  {{ copy.form.triggerLabel }}
                  <input
                    v-model="selectedNode.trigger.label"
                    :class="inputClass"
                    :placeholder="copy.labels.triggerLabelPlaceholder"
                  />
                </label>

                <label
                  v-if="selectedNode.trigger.type === 'message_contains'"
                  class="grid gap-2 text-sm font-medium"
                >
                  {{ copy.form.triggerKeyword }}
                  <input
                    v-model="selectedNode.trigger.keyword"
                    :class="inputClass"
                    :placeholder="copy.labels.triggerKeywordPlaceholder"
                  />
                </label>

                <label
                  v-if="selectedNode.trigger.type === 'inbox_changed'"
                  class="grid gap-2 text-sm font-medium"
                >
                  {{ copy.form.triggerInbox }}
                  <div class="relative">
                    <select
                      v-model="selectedNode.trigger.inboxId"
                      :class="selectClass"
                    >
                      <option value="">{{ copy.labels.noInbox }}</option>
                      <option
                        v-for="inbox in inboxOptions"
                        :key="inbox.value"
                        :value="inbox.value"
                      >
                        {{ inbox.label }}
                      </option>
                    </select>
                    <div
                      class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
                    >
                      <span
                        class="i-lucide-chevron-down size-4 text-n-slate-11"
                      />
                    </div>
                  </div>
                </label>

                <button
                  v-if="selectedNode.trigger.type !== 'webhook_received'"
                  type="button"
                  class="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-emerald-500/50 bg-emerald-500/15 px-3 text-sm font-medium text-emerald-200 hover:bg-emerald-500/25"
                  @click="useWebhookOnSelectedTrigger"
                >
                  <span class="i-lucide-radio-tower size-4" />
                  {{ copy.actions.useWebhookTrigger }}
                </button>

                <section
                  v-if="selectedNode.trigger.type === 'webhook_received'"
                  class="grid gap-3 rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3"
                >
                  <div class="grid gap-2 text-sm font-medium">
                    <span>{{ copy.form.triggerWebhookEndpoint }}</span>
                    <div class="flex gap-2">
                      <input
                        :value="webhookEndpointFor(selectedNode)"
                        :class="inputClass"
                        readonly
                      />
                      <button
                        type="button"
                        class="grid size-10 shrink-0 place-content-center rounded-lg border border-n-weak hover:border-n-brand hover:text-n-brand"
                        :title="copy.actions.copyWebhookUrl"
                        :aria-label="copy.actions.copyWebhookUrl"
                        @click="copyWebhookEndpoint(selectedNode)"
                      >
                        <span class="i-lucide-copy size-4" />
                      </button>
                      <button
                        type="button"
                        class="grid size-10 shrink-0 place-content-center rounded-lg border border-n-weak hover:border-n-brand hover:text-n-brand"
                        :title="copy.actions.refreshWebhook"
                        :aria-label="copy.actions.refreshWebhook"
                        @click="refreshCurrentFunnel"
                      >
                        <span class="i-lucide-refresh-cw size-4" />
                      </button>
                    </div>
                  </div>
                  <button
                    type="button"
                    class="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-emerald-500/50 bg-emerald-500/15 px-3 text-sm font-medium text-emerald-200 hover:bg-emerald-500/25 disabled:cursor-not-allowed disabled:opacity-60"
                    :disabled="testRun.isRunning"
                    @click="waitForWebhookAndTest(selectedNode)"
                  >
                    <span
                      class="size-4"
                      :class="
                        testRun.isRunning
                          ? 'i-lucide-loader-2 animate-spin'
                          : 'i-lucide-satellite-dish'
                      "
                    />
                    {{ copy.actions.waitWebhookTest }}
                  </button>

                  <label class="grid gap-2 text-sm font-medium">
                    {{ copy.form.triggerWebhookMethod }}
                    <div class="relative">
                      <select
                        v-model="selectedNode.trigger.webhookMethod"
                        :class="selectClass"
                      >
                        <option
                          v-for="method in webhookMethods"
                          :key="method"
                          :value="method"
                        >
                          {{ method }}
                        </option>
                      </select>
                      <div
                        class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
                      >
                        <span
                          class="i-lucide-chevron-down size-4 text-n-slate-11"
                        />
                      </div>
                    </div>
                  </label>

                  <label class="grid gap-2 text-sm font-medium">
                    {{ copy.form.triggerWebhookSample }}
                    <textarea
                      v-model="selectedNode.trigger.webhookSample"
                      rows="6"
                      class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 font-mono text-xs outline-none focus:border-n-brand"
                    />
                  </label>

                  <div class="grid gap-3 sm:grid-cols-3">
                    <label class="grid gap-2 text-sm font-medium">
                      {{ copy.form.jsonContactPath }}
                      <input
                        v-model="selectedNode.trigger.contactPath"
                        :class="inputClass"
                        :placeholder="copy.labels.jsonPathPlaceholder"
                      />
                    </label>
                    <label class="grid gap-2 text-sm font-medium">
                      {{ copy.form.jsonPhonePath }}
                      <input
                        v-model="selectedNode.trigger.phonePath"
                        :class="inputClass"
                        :placeholder="copy.labels.jsonPathPlaceholder"
                      />
                    </label>
                    <label class="grid gap-2 text-sm font-medium">
                      {{ copy.form.jsonMessagePath }}
                      <input
                        v-model="selectedNode.trigger.messagePath"
                        :class="inputClass"
                        :placeholder="copy.labels.jsonPathPlaceholder"
                      />
                    </label>
                  </div>

                  <div
                    class="rounded-lg border border-dashed border-n-weak p-3"
                  >
                    <div class="flex items-center justify-between gap-3">
                      <p class="text-xs font-medium text-n-slate-10">
                        {{
                          selectedWebhookLastFields.length
                            ? copy.labels.jsonLastFields
                            : copy.labels.jsonDetectedFields
                        }}
                      </p>
                      <span
                        v-if="selectedNode.trigger.lastReceivedAt"
                        class="text-[11px] text-n-slate-10"
                      >
                        {{ selectedNode.trigger.lastReceivedAt }}
                      </span>
                    </div>
                    <div
                      v-if="selectedWebhookFieldOptions.length"
                      class="mt-2 flex flex-wrap gap-2"
                    >
                      <button
                        v-for="field in selectedWebhookFieldOptions"
                        :key="field"
                        type="button"
                        class="rounded-md border border-n-weak px-2 py-1 font-mono text-[11px] text-n-slate-11 hover:border-n-brand hover:text-n-brand"
                        @click="selectedNode.trigger.messagePath = field"
                      >
                        {{ field }}
                      </button>
                    </div>
                    <p v-else class="mt-2 text-xs text-n-slate-10">
                      {{ copy.labels.webhookNoPayload }}
                    </p>
                  </div>

                  <label
                    v-if="selectedWebhookPayloadPreview"
                    class="grid gap-2 text-sm font-medium"
                  >
                    {{ copy.form.triggerWebhookLastPayload }}
                    <textarea
                      :value="selectedWebhookPayloadPreview"
                      rows="6"
                      readonly
                      class="rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 font-mono text-xs outline-none"
                    />
                  </label>
                </section>

                <button
                  type="button"
                  class="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-n-brand px-3 text-sm font-medium text-white hover:brightness-95"
                  @click="addConnectedNodeAfter(selectedNode, 'action')"
                >
                  <span class="i-lucide-plus size-4" />
                  {{ copy.actions.addNextAction }}
                </button>
              </section>

              <section
                v-if="selectedNode.type !== 'trigger'"
                class="grid gap-3 rounded-lg border border-n-weak bg-n-alpha-1 p-3"
              >
                <p class="text-sm font-semibold">
                  {{ copy.labels.actionSettings }}
                </p>

                <label class="grid gap-2 text-sm font-medium">
                  {{ copy.form.actionType }}
                  <div class="relative">
                    <select
                      v-model="selectedNode.action.type"
                      :class="selectClass"
                      @change="syncActionDefaults(selectedNode)"
                    >
                      <option
                        v-for="action in actionOptions"
                        :key="action.value"
                        :value="action.value"
                      >
                        {{ action.label }}
                      </option>
                    </select>
                    <div
                      class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
                    >
                      <span
                        class="i-lucide-chevron-down size-4 text-n-slate-11"
                      />
                    </div>
                  </div>
                </label>

                <label
                  v-if="
                    ['send_message', 'send_file'].includes(
                      selectedNode.action.type
                    )
                  "
                  class="grid gap-2 text-sm font-medium"
                >
                  {{ copy.form.actionMessage }}
                  <textarea
                    v-model="selectedNode.action.message"
                    rows="3"
                    class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm outline-none focus:border-n-brand"
                    :placeholder="copy.labels.jsonVariablesHint"
                  />
                </label>

                <section
                  v-if="selectedNode.action.type === 'send_message'"
                  class="grid gap-3 rounded-lg border border-n-weak bg-n-solid-1 p-3"
                >
                  <div>
                    <p class="text-sm font-semibold">
                      {{ copy.labels.sendMessageTarget }}
                    </p>
                    <p class="mt-1 text-xs text-n-slate-10">
                      {{ copy.labels.sendMessageTargetHelp }}
                    </p>
                  </div>
                  <div class="grid gap-3 sm:grid-cols-3">
                    <label class="grid gap-2 text-sm font-medium">
                      {{ copy.form.actionPhonePath }}
                      <input
                        v-model="selectedNode.action.phonePath"
                        :class="inputClass"
                        :placeholder="copy.labels.jsonPathPlaceholder"
                      />
                    </label>
                    <label class="grid gap-2 text-sm font-medium">
                      {{ copy.form.actionContactPath }}
                      <input
                        v-model="selectedNode.action.contactPath"
                        :class="inputClass"
                        :placeholder="copy.labels.jsonPathPlaceholder"
                      />
                    </label>
                    <label class="grid gap-2 text-sm font-medium">
                      {{ copy.form.actionMessagePath }}
                      <input
                        v-model="selectedNode.action.messagePath"
                        :class="inputClass"
                        :placeholder="copy.labels.jsonPathPlaceholder"
                      />
                    </label>
                  </div>
                  <div
                    v-if="selectedActionWebhookFieldOptions.length"
                    class="flex flex-wrap gap-2"
                  >
                    <button
                      v-for="field in selectedActionWebhookFieldOptions"
                      :key="field"
                      type="button"
                      class="rounded-md border border-n-weak px-2 py-1 font-mono text-[11px] text-n-slate-11 hover:border-n-brand hover:text-n-brand"
                      @click="insertActionJsonVariable(field)"
                    >
                      {{ field }}
                    </button>
                  </div>
                </section>

                <label
                  v-if="selectedNode.action.type === 'send_file'"
                  class="grid gap-2 text-sm font-medium"
                >
                  {{ copy.form.actionFile }}
                  <input
                    type="file"
                    class="rounded-lg border border-dashed border-n-weak bg-n-solid-1 px-3 py-2 text-sm"
                    @change="handleActionFileUpload"
                  />
                  <span v-if="isUploadingFile" class="text-xs text-amber-700">
                    {{ copy.status.saving }}
                  </span>
                  <span
                    v-else-if="selectedNode.action.file?.name"
                    class="text-xs text-emerald-700"
                  >
                    {{ copy.labels.uploadReady }}
                    <span class="font-medium">{{
                      selectedNode.action.file.name
                    }}</span>
                  </span>
                  <span v-if="uploadError" class="text-xs text-ruby-700">
                    {{ copy.labels.uploadError }}
                  </span>
                </label>

                <label
                  v-if="selectedNode.action.type === 'send_file'"
                  class="grid gap-2 text-sm font-medium"
                >
                  {{ copy.form.actionFileCaption }}
                  <input
                    v-model="selectedNode.action.fileCaption"
                    :class="inputClass"
                    :placeholder="copy.labels.fileCaptionPlaceholder"
                  />
                </label>

                <label
                  v-if="selectedNode.action.type === 'call_webhook'"
                  class="grid gap-2 text-sm font-medium"
                >
                  {{ copy.form.webhookUrl }}
                  <input
                    v-model="selectedNode.action.webhookUrl"
                    :class="inputClass"
                    :placeholder="copy.labels.webhookPlaceholder"
                  />
                </label>

                <label
                  v-if="selectedNode.action.type === 'call_webhook'"
                  class="grid gap-2 text-sm font-medium"
                >
                  {{ copy.form.webhookMethod }}
                  <div class="relative">
                    <select
                      v-model="selectedNode.action.webhookMethod"
                      :class="selectClass"
                    >
                      <option
                        v-for="method in webhookMethods"
                        :key="method"
                        :value="method"
                      >
                        {{ method }}
                      </option>
                    </select>
                    <div
                      class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
                    >
                      <span
                        class="i-lucide-chevron-down size-4 text-n-slate-11"
                      />
                    </div>
                  </div>
                </label>

                <label
                  v-if="selectedNode.action.type === 'run_credit_engine'"
                  class="grid gap-2 text-sm font-medium"
                >
                  {{ copy.form.creditEngine }}
                  <div class="relative">
                    <select
                      v-model="selectedNode.action.creditEngineId"
                      :class="selectClass"
                    >
                      <option value="">{{ copy.labels.noCreditEngine }}</option>
                      <option
                        v-for="engine in creditEngineOptions"
                        :key="engine.value"
                        :value="engine.value"
                      >
                        {{ engine.label }}
                      </option>
                    </select>
                    <div
                      class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
                    >
                      <span
                        class="i-lucide-chevron-down size-4 text-n-slate-11"
                      />
                    </div>
                  </div>
                  <span
                    v-if="!creditEngineOptions.length"
                    class="text-xs text-n-slate-10"
                  >
                    {{ copy.labels.noCreditEngines }}
                  </span>
                </label>

                <div
                  v-if="selectedNode.action.type === 'run_credit_engine'"
                  class="grid gap-3 rounded-lg border border-n-weak bg-n-solid-1 p-3"
                >
                  <p class="mb-0 text-xs leading-5 text-n-slate-11">
                    {{ copy.labels.creditEngineHelp }}
                  </p>
                  <div
                    v-if="selectedCreditEngine"
                    class="grid gap-1 rounded-lg bg-n-alpha-2 p-3 text-xs"
                  >
                    <strong class="text-n-slate-12">
                      {{ selectedCreditEngineLabel }}
                    </strong>
                    <span class="text-n-slate-10">
                      {{ selectedCreditEngine.eligibility_summary }}
                    </span>
                  </div>
                  <button
                    type="button"
                    class="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-n-weak px-3 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2"
                    @click="openCreditEngineSettings"
                  >
                    <span class="i-lucide-settings-2 size-4" />
                    {{ copy.labels.openCreditEngine }}
                  </button>
                </div>

                <label
                  v-if="selectedNode.action.type === 'add_label'"
                  class="grid gap-2 text-sm font-medium"
                >
                  {{ copy.labels.labelField }}
                  <input
                    v-model="selectedNode.action.label"
                    :class="inputClass"
                    :placeholder="copy.labels.labelPlaceholder"
                  />
                </label>

                <label
                  v-if="selectedNode.action.type === 'move_inbox'"
                  class="grid gap-2 text-sm font-medium"
                >
                  {{ copy.labels.targetInbox }}
                  <div class="relative">
                    <select
                      v-model="selectedNode.action.targetInboxId"
                      :class="selectClass"
                    >
                      <option value="">{{ copy.labels.noInbox }}</option>
                      <option
                        v-for="inbox in inboxOptions"
                        :key="inbox.value"
                        :value="inbox.value"
                      >
                        {{ inbox.label }}
                      </option>
                    </select>
                    <div
                      class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
                    >
                      <span
                        class="i-lucide-chevron-down size-4 text-n-slate-11"
                      />
                    </div>
                  </div>
                </label>

                <label
                  v-if="
                    [
                      'send_message',
                      'send_file',
                      'request_best_offer',
                      'run_credit_engine',
                    ].includes(selectedNode.action.type)
                  "
                  class="grid gap-2 text-sm font-medium"
                >
                  {{ copy.form.actionDelay }}
                  <input
                    v-model.number="selectedNode.action.delayMinutes"
                    type="number"
                    min="0"
                    :class="inputClass"
                  />
                </label>
              </section>

              <div class="grid gap-3 sm:grid-cols-2">
                <label class="grid gap-2 text-sm font-medium">
                  {{ copy.form.leads }}
                  <input
                    v-model="selectedNodeLeadsDisplay"
                    type="text"
                    inputmode="numeric"
                    :class="inputClass"
                  />
                </label>
                <label class="grid gap-2 text-sm font-medium">
                  {{ copy.form.conversion }}
                  <input
                    v-model="selectedNodeConversionDisplay"
                    type="text"
                    inputmode="decimal"
                    :class="inputClass"
                  />
                </label>
                <label class="grid gap-2 text-sm font-medium">
                  {{ copy.form.investment }}
                  <input
                    v-model="selectedNodeInvestmentDisplay"
                    type="text"
                    inputmode="decimal"
                    :class="inputClass"
                  />
                </label>
                <label class="grid gap-2 text-sm font-medium">
                  {{ copy.form.potential }}
                  <input
                    v-model="selectedNodePotentialDisplay"
                    type="text"
                    inputmode="decimal"
                    :class="inputClass"
                  />
                </label>
              </div>

              <div class="flex gap-2 border-t border-n-weak pt-4">
                <button
                  type="button"
                  class="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-lg border border-n-weak px-3 text-sm font-medium hover:bg-n-alpha-2"
                  @click="duplicateNode(selectedNode)"
                >
                  <span class="i-lucide-copy size-4" />
                  {{ copy.actions.duplicate }}
                </button>
                <button
                  type="button"
                  class="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-lg bg-n-brand px-3 text-sm font-medium text-white hover:brightness-95"
                  @click="openLinkedResource(selectedNode)"
                >
                  <span class="i-lucide-external-link size-4" />
                  {{ copy.actions.openLink }}
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </section>
    <div
      v-if="nodePicker.visible"
      class="fixed inset-0 z-50 grid place-items-center bg-n-slate-12/50 px-4 py-6"
      @click.self="closeNodePicker"
    >
      <section
        class="flex max-h-[82vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-n-weak bg-n-solid-1 shadow-2xl"
      >
        <header
          class="flex items-center justify-between gap-3 border-b border-n-weak px-5 py-4"
        >
          <div class="min-w-0">
            <p class="text-lg font-semibold">{{ copy.labels.nextNodeTitle }}</p>
            <p class="mt-1 text-sm text-n-slate-10">
              {{ copy.labels.nextNodeDescription }}
            </p>
          </div>
          <button
            type="button"
            class="grid size-9 place-content-center rounded-lg border border-n-weak hover:bg-n-alpha-2"
            :aria-label="copy.actions.close"
            @click="closeNodePicker"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </header>

        <div class="min-h-0 overflow-y-auto p-5">
          <label class="relative block">
            <span
              class="i-lucide-search absolute left-3 top-1/2 size-4 -translate-y-1/2 text-n-slate-10"
            />
            <input
              v-model="nodePicker.search"
              :class="inputClass"
              class="pl-9"
              :placeholder="copy.labels.nextNodeSearch"
            />
          </label>

          <div class="mt-4 grid gap-2">
            <button
              v-for="option in filteredNextNodeOptions"
              :key="`${option.type}-${option.actionType}`"
              type="button"
              class="flex items-center gap-3 rounded-lg border border-n-weak p-3 text-left transition hover:border-n-brand hover:bg-n-alpha-2"
              @click="addNodeFromPicker(option)"
            >
              <span
                class="grid size-10 shrink-0 place-content-center rounded-lg bg-n-alpha-2 text-n-brand"
              >
                <span class="size-5" :class="[option.icon]" />
              </span>
              <span class="min-w-0 flex-1">
                <span class="block text-sm font-semibold">{{
                  option.title
                }}</span>
                <span class="mt-1 block text-xs leading-5 text-n-slate-10">
                  {{ option.description }}
                </span>
              </span>
              <span class="i-lucide-arrow-right size-4 text-n-slate-10" />
            </button>
          </div>
        </div>
      </section>
    </div>

    <div
      v-if="testRun.isModalOpen"
      class="fixed inset-0 z-50 grid place-items-center bg-n-slate-12/40 px-4 py-6"
      @click.self="closeTestModal"
    >
      <section
        class="flex max-h-[82vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg border border-n-weak bg-n-solid-1 shadow-2xl"
      >
        <header
          class="flex items-center justify-between gap-3 border-b border-n-weak px-5 py-4"
        >
          <div class="min-w-0">
            <p class="text-base font-semibold">{{ copy.labels.testRun }}</p>
            <p class="mt-1 text-sm text-n-slate-10">
              {{ copy.labels.testModalDescription }}
            </p>
          </div>
          <button
            type="button"
            class="grid size-9 place-content-center rounded-lg border border-n-weak hover:bg-n-alpha-2"
            :aria-label="copy.actions.close"
            @click="closeTestModal"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </header>

        <div class="min-h-0 overflow-y-auto p-5">
          <div
            v-if="!testRun.logs.length"
            class="rounded-lg border border-dashed border-n-weak p-4 text-sm text-n-slate-10"
          >
            {{ copy.labels.testIdle }}
          </div>
          <div v-else class="grid gap-3">
            <article
              v-for="log in testRun.logs"
              :key="log.id"
              class="rounded-lg border px-4 py-3"
              :style="testLogStyle(log.status)"
            >
              <div class="flex items-center gap-2 text-sm font-semibold">
                <span
                  class="size-4"
                  :class="
                    log.status === 'error'
                      ? 'i-lucide-x-circle'
                      : 'i-lucide-check-circle-2'
                  "
                />
                <span class="truncate">{{ log.title }}</span>
              </div>
              <p class="mt-2 text-sm opacity-80">{{ log.detail }}</p>
              <p v-if="log.summary" class="mt-1 text-sm">
                {{ log.summary }}
              </p>
              <p v-if="log.hint" class="mt-2 text-xs font-medium">
                {{ log.hint }}
              </p>
            </article>
          </div>
        </div>

        <footer class="flex justify-end gap-2 border-t border-n-weak px-5 py-4">
          <button
            type="button"
            class="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-n-weak px-3 text-sm font-medium hover:bg-n-alpha-2"
            @click="clearTestRun"
          >
            <span class="i-lucide-rotate-ccw size-4" />
            {{ copy.actions.clearTest }}
          </button>
          <button
            type="button"
            class="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-n-brand px-3 text-sm font-medium text-white hover:brightness-95"
            @click="runTestFlow"
          >
            <span class="i-lucide-play-circle size-4" />
            {{ copy.actions.testFlow }}
          </button>
        </footer>
      </section>
    </div>
  </main>
</template>

<style scoped>
.node-flow-summary {
  @apply grid grid-cols-1 gap-3 md:grid-cols-3;
}

.node-flow-summary > div {
  @apply min-w-0 rounded-xl border border-n-weak bg-n-alpha-1 p-3;
}

.node-flow-summary span {
  @apply text-n-brand;
}

.node-flow-summary p {
  @apply mb-1 mt-2 text-xs font-semibold uppercase text-n-slate-10;
}

.node-flow-summary strong {
  @apply line-clamp-2 text-[13px] font-semibold leading-5 text-n-slate-12;
}

.flow-side-panel.is-collapsed {
  @apply items-center;
}

.flow-side-panel.is-collapsed .flow-start-panel {
  @apply w-full border-b-0 p-2;
}

.flow-side-panel.is-collapsed .flow-start-panel > :not(button) {
  @apply hidden;
}

.flow-side-panel.is-collapsed .flow-start-panel > button {
  @apply mx-auto mb-0;
}

.node-editor-modal {
  @apply fixed inset-0 z-50;
}

.node-editor-backdrop {
  @apply absolute inset-0 backdrop-blur-sm;
  background: rgba(0, 0, 0, 0.6);
}

.node-editor-dialog {
  @apply absolute inset-3 flex flex-col overflow-hidden rounded-xl border border-n-weak bg-n-solid-1 shadow-2xl;
}

@media (min-width: 768px) {
  .node-editor-dialog {
    @apply inset-x-8 inset-y-6;
  }
}

@media (min-width: 1280px) {
  .node-editor-dialog {
    @apply inset-x-20 inset-y-8;
  }
}
</style>
